import React, { useEffect, useState } from "react";
import ShippingResolutionCard from "@/components/ShippingResolutionCard";
import { RotateCcw as RebookBadgeIcon, Paperclip as EvidenceIcon } from "lucide-react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, downloadTaskInvoicePdf, downloadTaskPackingSlip } from "@/lib/api";
import TaskRouteMap from "@/components/TaskRouteMap";
import IncidentWarning from "@/components/IncidentWarning";
import { useRouteIncidents } from "@/hooks/useRouteIncidents";
import { useAuth } from "@/context/AuthContext";
import { Pill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion"; // eslint-disable-line no-unused-vars
import { toast } from "sonner";
import { DEPT_COLOUR, DEPT_TEAM_LABEL, DEPT_TEXT } from "@/lib/departments";
import CostForm from "@/components/CostForm";
import JobForm from "@/components/JobForm";
import TaskFiles from "@/components/TaskFiles";
import TaskTodos from "@/components/TaskTodos";
import TaskComments from "@/components/TaskComments";
import JobSmsPanel from "@/components/JobSmsPanel";
import TaskInvoices from "@/components/TaskInvoices";
import CustomerPayment from "@/components/CustomerPayment";
import JobPaymentsSummary from "@/components/JobPaymentsSummary";
import ShiftSummaryPanel from "@/components/scheduler/ShiftSummaryPanel";
import ShiftForm from "@/components/scheduler/ShiftForm";
import DaySummaryPanel from "@/components/scheduler/DaySummaryPanel";
import FormFiller from "@/components/FormFiller";
import { SourceBadge, EnquiryDetails } from "@/components/EnquirySource";
import S3Uploader from "@/components/S3Uploader";
import { CostReceiptsButton, CostBreakdownRows } from "@/components/CostReceipts";
import SignaturePad from "@/components/SignaturePad";
import { JobLoadedItems } from "@/components/JobLoadedItems";
import { JobPickedItems, useJobPicked } from "@/components/JobPickedItems";
import { JobActionsMenu } from "@/components/JobQuickLinks";
import { JobOverview, venueAddress } from "@/components/JobOverview";
import { JobStatusSelect } from "@/components/JobStatusSelect";
import { SalesIntelCard } from "@/components/SalesIntelCard";
import { AllocateProductsDialog } from "@/components/AllocateProductsDialog";
import { formatDateTime } from "@/lib/datetime";
import {
  Loader2, MapPin, CheckCircle2, Menu, ChevronDown, Printer, Pencil,
  Copy, Download, Zap, ListChecks, FilePlus, Send, CircleDot, Info, AlertTriangle, PoundSterling,
  ShieldAlert, Clock, User, Lock, Truck, CloudRain, Droplets, Thermometer, Navigation, Package, ChevronRight,
  ArrowRight, GitBranch, Plus, Trash2, Receipt, CreditCard, FolderKanban, Briefcase, X, Check,
} from "lucide-react";

const TABS = ["Detail", "Shifts", "Requirements", "Finance", "Highlights", "Map", "More Information"];

function CollapsibleCard({ title, children, testid, defaultOpen = true }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid={testid}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between text-left" data-testid={testid ? `${testid}-toggle` : undefined}>
        <h2 className="text-base font-bold text-slate-900">{title}</h2>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && <div className="mt-3">{children}</div>}
    </section>
  );
}

function HighlightsTab({ timeline = [] }) {
  const [showAll, setShowAll] = useState(false);
  const items = showAll ? timeline : timeline.slice(0, 5);
  return (
    <div className="p-6 lg:p-8 mx-auto" data-testid="task-highlights">
      <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-4">Timeline</h2>
        {timeline.length === 0 ? (
          <div className="text-center text-sm text-slate-400 py-6">No timeline activity yet.</div>
        ) : (
          <>
            <ol className="relative border-l border-slate-200 ml-2 space-y-5">
              {items.map((e, i) => (
                <li key={i} className="ml-4" data-testid={`highlight-item-${i}`}>
                  <span className="absolute -left-[6.5px] w-3 h-3 rounded-full bg-indigo-400 border-2 border-white" />
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                      <span className="text-sm font-semibold text-slate-900">{e.title}</span>
                    </div>
                    <span className="text-xs text-slate-400 whitespace-nowrap">{e.ts}</span>
                  </div>
                  {e.detail && <div className="text-xs text-slate-500 mt-0.5 ml-5">{e.detail}</div>}
                </li>
              ))}
            </ol>
            {timeline.length > 5 && (
              <div className="flex justify-center mt-5">
                <Button variant="outline" size="sm" onClick={() => setShowAll((v) => !v)} data-testid="highlights-view-more">
                  {showAll ? "Show less" : "View more"}
                </Button>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function MoreInfoPanel({ title, icon: Icon, badge, action, children, defaultOpen = true, testid }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid={testid || `moreinfo-panel-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 px-5 py-3.5 bg-slate-50/70 border-b border-slate-100">
        <button onClick={() => setOpen((o) => !o)} className="flex-1 flex items-center gap-2 text-left text-base font-bold text-slate-900">
          {Icon && <Icon className="w-4 h-4 text-indigo-500" />}
          {title}
          {badge && <span className="text-xs font-medium text-indigo-700 bg-indigo-100 rounded-full px-2.5 py-0.5">{badge}</span>}
        </button>
        {action}
        <button onClick={() => setOpen((o) => !o)} className="text-slate-400 hover:text-slate-600">
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "" : "-rotate-90"}`} />
        </button>
      </div>
      {open && <div className="px-5 py-5">{children}</div>}
    </div>
  );
}

function MoreInfoEmpty({ text }) {
  return <div className="text-center py-2"><span className="text-sm text-slate-500 bg-slate-100 rounded-full px-3 py-1" data-testid="moreinfo-empty">{text}</span></div>;
}

function MoreInfoTab({ legacyId, assignments = [], moreInfo = {} }) {
  const MI_CATS = [
    ["faults", "Equipment Faults", "No Faults Found"],
    ["incidents", "Incidents", "No Incidents Found"],
    ["reserved_equipment", "Reserved Equipment", "No Equipment Reservations Found"],
    ["equipment_used", "Equipment Used", "No Equipment Usages Found"],
  ];
  const ADD_TITLES = { faults: "Equipment Fault", incidents: "Incident", reserved_equipment: "Reserved Equipment", equipment_used: "Equipment Used", products: "Product" };
  const [data, setData] = useState(moreInfo || {});
  const [adding, setAdding] = useState(null);
  const [form, setForm] = useState({ title: "", description: "" });
  const [busy, setBusy] = useState(false);
  const list = (k) => data[k] || [];
  const openAdd = (k) => { setAdding(k); setForm({ title: "", description: "" }); };
  const save = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/tasks/${legacyId}/moreinfo/${adding}`, form);
      setData((s) => ({ ...s, [adding]: [...(s[adding] || []), r.data.item] }));
      setAdding(null); toast.success("Added");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
    finally { setBusy(false); }
  };
  const del = async (k, itemId) => {
    try { await api.delete(`/tasks/${legacyId}/moreinfo/${k}/${itemId}`); setData((s) => ({ ...s, [k]: (s[k] || []).filter((x) => x.id !== itemId) })); }
    catch { toast.error("Could not remove"); }
  };
  const addBtn = (k) => <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openAdd(k)} data-testid={`moreinfo-add-${k}`}>Add</Button>;
  const Items = ({ k }) => list(k).length === 0 ? null : (
    <div className="space-y-2 mb-2">
      {list(k).map((it) => (
        <div key={it.id} className="flex items-start justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2" data-testid={`moreinfo-item-${it.id}`}>
          <div className="min-w-0"><div className="text-sm font-semibold text-slate-800">{it.title}</div>{it.description && <div className="text-xs text-slate-500 mt-0.5">{it.description}</div>}</div>
          <button onClick={() => del(k, it.id)} className="text-slate-300 hover:text-rose-500 shrink-0"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
  return (
    <div className="p-6 lg:p-8 mx-auto space-y-4" data-testid="task-more-info">
      {MI_CATS.map(([k, title, empty]) => (
        <MoreInfoPanel key={k} title={title} action={addBtn(k)}>
          <Items k={k} />
          {list(k).length === 0 && <MoreInfoEmpty text={empty} />}
        </MoreInfoPanel>
      ))}
      <MoreInfoPanel title="Products" action={addBtn("products")} badge={assignments.length ? `${assignments.length} Assignment${assignments.length > 1 ? "s" : ""}` : null}>
        <Items k="products" />
        {assignments.length === 0 ? (
          <MoreInfoEmpty text="No Products Assigned" />
        ) : (
          <div className="rounded-lg border border-slate-200 overflow-hidden">
            <table className="w-full text-sm" data-testid="moreinfo-products-table">
              <thead><tr className="bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                <th className="px-3 py-2 w-24">ID</th><th className="px-3 py-2">Product</th>
                <th className="px-3 py-2 w-24">Stock ID</th><th className="px-3 py-2 w-40">Assigned By</th>
                <th className="px-3 py-2 w-32">Infrastructure ID</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {assignments.map((p, i) => (
                  <tr key={p.id || i} data-testid={`moreinfo-product-${i}`}>
                    <td className="px-3 py-2.5 font-mono text-slate-500">{p.id || p.legacy_id || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-800 font-medium">{p.product || p.title || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-600">{p.stock_id || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-600">{p.assigned_by || "—"}</td>
                    <td className="px-3 py-2.5 text-slate-600">{p.infrastructure_id || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-3 py-2 text-xs text-slate-500 bg-white border-t border-slate-100">
              Showing <strong>1</strong> to <strong>{assignments.length}</strong> of <strong>{assignments.length}</strong> results
            </div>
          </div>
        )}
      </MoreInfoPanel>
      {adding && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex justify-end" onClick={() => setAdding(null)} data-testid="moreinfo-add-modal">
          <div className="bg-white h-full w-full sm:w-1/2 sm:max-w-none shadow-2xl p-6 flex flex-col gap-4 animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900">Add {ADD_TITLES[adding] || "Item"}</h3>
            <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Title</label>
              <Input value={form.title} onChange={(e) => setForm((s) => ({ ...s, title: e.target.value }))} className="mt-1" data-testid="moreinfo-title" /></div>
            <div><label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Description</label>
              <textarea value={form.description} onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))} rows={4} className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-50 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100" data-testid="moreinfo-description" /></div>
            <div className="mt-auto flex justify-end gap-2">
              <Button variant="outline" onClick={() => setAdding(null)}>Cancel</Button>
              <Button className="rounded-[8px] h-10 px-4 bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={save} data-testid="moreinfo-save">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
const JOB_STATUSES = ["Awaiting Deposit", "Awaiting Final Payment", "Paid in Full", "Products Allocated", "On Hold", "Complete", "Cancelled"];
const DEPT_STATUSES = ["Quote Approved", "Quote Pending", "Awaiting Stock", "Ready", "Dispatched", "Complete"];
const ENQUIRY_STAGES = ["New", "Awaiting Quote", "Quote Sent", "Customer will call back", "Voicemail left", "Waiting Customer response", "Won", "Abandoned"];

const ALERT_TONE = {
  success: { wrap: "bg-emerald-50 border-emerald-200", icon: "text-emerald-600", Icon: CheckCircle2 },
  warning: { wrap: "bg-amber-50 border-amber-200", icon: "text-amber-600", Icon: AlertTriangle },
  danger: { wrap: "bg-rose-50 border-rose-200", icon: "text-rose-600", Icon: ShieldAlert },
  info: { wrap: "bg-blue-50 border-blue-200", icon: "text-blue-600", Icon: Info },
};

function Val({ children }) {
  if (children === "••••••••") return <span className="text-slate-400 tracking-widest">••••••••</span>;
  return children ? <span className="text-slate-800">{children}</span> : <span className="text-slate-300">—</span>;
}

function EmptyBlock({ text }) {
  return <div className="text-center text-sm text-slate-400 py-6 bg-slate-50/60 rounded-lg border border-dashed border-slate-200">{text}</div>;
}

function RebookEvidenceThumb({ item }) {
  const [url, setUrl] = useState(null);
  const isImg = (item.mime || "").startsWith("image/");
  useEffect(() => {
    let c = false;
    if (item.downloadable) api.get(`/attachments/${item.id}/view`).then((r) => { if (!c) setUrl(r.data?.url || null); }).catch(() => {});
    return () => { c = true; };
  }, [item.id, item.downloadable]);
  return (
    <button type="button" onClick={() => url && window.open(url, "_blank")} disabled={!item.downloadable}
      title={item.filename} data-testid={`rebook-evidence-${item.id}`}
      className="group relative w-16 h-16 rounded-lg overflow-hidden border border-amber-200 bg-amber-50 flex items-center justify-center hover:border-amber-400 transition-colors disabled:opacity-60">
      {url && isImg ? <img src={url} alt={item.filename} className="w-full h-full object-cover" loading="lazy" />
        : <EvidenceIcon className="w-5 h-5 text-amber-500" />}
      {!item.downloadable && <span className="absolute inset-0 bg-white/70 text-[9px] text-amber-700 flex items-center justify-center text-center px-1">{item.scan_status === "SCANNING" ? "Scanning…" : "Blocked"}</span>}
    </button>
  );
}

function RebookPanel({ rebook, evidence = [] }) {
  if (!rebook) return null;
  const when = rebook.at ? new Date(rebook.at).toLocaleString("en-GB") : null;
  return (
    <section data-testid="rebook-panel" className="bg-amber-50 border border-amber-300 rounded-2xl shadow-sm p-5">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span data-testid="rebook-badge" className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide rounded-full px-3 py-1 bg-amber-500 text-white"><RebookBadgeIcon className="w-3.5 h-3.5" /> Rebook</span>
        <span className="text-sm text-amber-800">This visit didn’t complete — Operations will arrange a new appointment.</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <div className="sm:col-span-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700/80">Reason</div>
          <div className="text-slate-800 font-medium" data-testid="rebook-reason">{rebook.reason || "—"}</div>
        </div>
        {rebook.note && (
          <div className="sm:col-span-2">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700/80">Engineer note</div>
            <div className="text-slate-700 whitespace-pre-line" data-testid="rebook-note">{rebook.note}</div>
          </div>
        )}
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700/80">Engineer</div>
          <div className="text-slate-800" data-testid="rebook-engineer">{rebook.by_name || "—"}</div>
        </div>
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700/80">Rebooked</div>
          <div className="text-slate-800" data-testid="rebook-at">{when || "—"}</div>
        </div>
      </div>
      {evidence.length > 0 && (
        <div className="mt-4">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-amber-700/80 mb-2">Evidence ({evidence.length})</div>
          <div className="flex flex-wrap gap-2" data-testid="rebook-evidence-list">
            {evidence.map((it) => <RebookEvidenceThumb key={it.id} item={it} />)}
          </div>
        </div>
      )}
    </section>
  );
}

export default function TaskDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, masking } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState("Detail");
  const [editOpen, setEditOpen] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState("");
  const [moveNote, setMoveNote] = useState("");
  const [moveAssignee, setMoveAssignee] = useState("none");
  const [assignees, setAssignees] = useState([]);
  const [moving, setMoving] = useState(false);
  const [shiftSummaryId, setShiftSummaryId] = useState(null);
  const [shiftEdit, setShiftEdit] = useState(null);
  const [shiftCreate, setShiftCreate] = useState(false);
  const [dayView, setDayView] = useState(null);
  const [wb, setWb] = useState(null);
  const [finCosts, setFinCosts] = useState(0);
  const [delOpen, setDelOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = () =>
    api.get(`/tasks/${id}`, { params: { mask: masking } })
      .then((r) => setData(r.data))
      .catch(() => setError(true));

  useEffect(() => {
    setData(null); setError(false); load();
    api.post(`/tasks/${id}/inbound-seen`).catch(() => {});
    api.get(`/quotes/by-task/${id}`).then((r) => setQuotes(r.data.items)).catch(() => setQuotes([]));
    api.get(`/work-bank/for-job/${id}`).then((r) => setWb(r.data)).catch(() => setWb(null));
    api.get(`/tasks/${id}/costs`).then((r) => setFinCosts((r.data.items || []).reduce((a, c) => a + (c.total || 0), 0))).catch(() => setFinCosts(0));
    /* eslint-disable-next-line */
  }, [id, masking]);

  useEffect(() => {
    if (!moveOpen || !moveTarget) { setAssignees([]); return; }
    setMoveAssignee("none");
    api.get(`/tasks-departments/${moveTarget}/assignees`)
      .then((r) => setAssignees(r.data.assignees || []))
      .catch(() => setAssignees([]));
  }, [moveOpen, moveTarget]);

  const patch = async (fields, msg) => {
    try {
      await api.patch(`/tasks/${id}`, fields);
      toast.success(msg || "Job updated (demo database)");
      load();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
      return false;
    }
  };

  const recordDeposit = () => patch({ deposit_date: new Date().toISOString().slice(0, 10) }, "Deposit recorded");
  const [addFormOpen, setAddFormOpen] = useState(false);
  const downloadMedia = () => downloadJobMedia(id);

  if (error)
    return (
      <div className="p-10 text-center" data-testid="task-not-found">
        <p className="text-slate-500">Job <span className="font-mono">{id}</span> was not found.</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/tasks")}>Back to Jobs</Button>
      </div>
    );
  if (!data)
    return <div className="p-10 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading job…</div>;

  const t = data.task;
  const isWebSale = (t.task_type || "").toLowerCase() === "web sale" || t.origin === "woocommerce";
  const isShopSale = (t.task_type || "").toLowerCase() === "shop sale" || t.sales_source === "EPOS";
  const mv = data.movement || { can_move: false, allowed_targets: [] };
  const doMove = async () => {
    if (!moveTarget) return;
    setMoving(true);
    try {
      const body = {
        to_department: moveTarget,
        handover_note: moveNote || undefined,
        assignee_id: moveAssignee !== "none" ? moveAssignee : undefined,
      };
      const r = await api.post(`/tasks/${id}/move-department`, body);
      toast.success(r.data.message || "Job moved");
      setMoveOpen(false);
      navigate("/tasks");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not move job");
    } finally {
      setMoving(false);
    }
  };
  const opts = (list, cur) => (cur && !list.includes(cur) ? [cur, ...list] : list);
  const isEnquiry = t.current_department === "Enquiries";
  const showDeptStatus = isEnquiry || isShopSale || t.current_department === "Shop";
  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/tasks/${id}`);
      toast.success(`Job ${t.legacy_id} permanently deleted`);
      setDelOpen(false);
      navigate("/tasks");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete job");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div data-testid="task-detail">
      {/* Persistent task header */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight truncate" data-testid="task-header-title">
              {t.legacy_id}: {t.title}
            </h1>
            <p className="text-sm text-slate-500 mt-0.5 flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate" data-testid="task-header-address">at {venueAddress(t, data.end_user) || t.venue_name || "—"}</span>
            </p>
            {data.project && (
              <Link to={`/projects/${data.project.project_id}`} data-testid="task-project-chip"
                className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-indigo-50 border border-indigo-200 px-2.5 py-1 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 transition-colors">
                <FolderKanban className="w-3.5 h-3.5" /> {data.project.name}
                {data.project.phase_name && <span className="text-indigo-400 font-normal">· {data.project.phase_name}</span>}
              </Link>
            )}
            {data.outstanding_requests > 0 && (
              <Link to="/stock/product-requests" data-testid="task-outstanding-requests"
                className="mt-1.5 ml-2 inline-flex items-center gap-1.5 rounded-full bg-amber-50 border border-amber-300 px-2.5 py-1 text-xs font-semibold text-amber-800 hover:bg-amber-100 transition-colors">
                <Package className="w-3.5 h-3.5" /> {data.outstanding_requests} part{data.outstanding_requests > 1 ? "s" : ""} outstanding
              </Link>
            )}
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Department</label>
              <div className="flex items-center gap-2 h-9">
                {mv.can_move ? (
                  <Select value={t.current_department || undefined}
                    onValueChange={(v) => { if (v !== t.current_department) { setMoveTarget(v); setMoveNote(""); setMoveAssignee("none"); setMoveOpen(true); } }}>
                    <SelectTrigger className="h-9 w-44" data-testid="task-current-department">
                      <span className={`text-sm font-medium ${DEPT_TEXT[DEPT_COLOUR[t.current_department]] || "text-slate-800"}`}>{t.current_department || "—"}</span>
                    </SelectTrigger>
                    <SelectContent>
                      {[t.current_department, ...mv.allowed_targets.filter((d) => d !== t.current_department)].filter(Boolean).map((d) => (
                        <SelectItem key={d} value={d} data-testid={`task-dept-option-${d}`}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <span data-testid="task-current-department"><Pill colour={DEPT_COLOUR[t.current_department]}>{t.current_department || "—"}</Pill></span>
                )}
              </div>
            </div>
            {!isEnquiry && (
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Job Status</label>
              <JobStatusSelect value={t.status} disabled={!isAdmin} options={opts(JOB_STATUSES, t.status)}
                onChange={(v) => patch({ status: v }, "Job status updated")} />
            </div>
            )}
            {showDeptStatus && (
            <div>
              <label className="block text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">{isEnquiry ? "Enquiry Stage" : "Shop / Dept Status"}</label>
              <Select value={t.department_status || undefined} disabled={!isAdmin}
                onValueChange={(v) => patch({ department_status: v }, isEnquiry ? "Enquiry stage updated" : "Department status updated")}>
                <SelectTrigger className="h-9 w-44" data-testid="task-dept-status"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>{opts(isEnquiry ? ENQUIRY_STAGES : DEPT_STATUSES, t.department_status).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            )}
            <JobActionsMenu legacyId={id} endUser={data.end_user} contactName={t.public_contact} quotes={quotes} isAdmin={isAdmin}
              isComplete={[t.status, t.job_summary_status].some((s) => ["complete", "completed"].includes((s || "").trim().toLowerCase()))}
              onEdit={() => setEditOpen(true)} onCreateShift={() => setShiftCreate(true)}>
                <DropdownMenuItem onClick={() => window.open(`/tasks/${id}/print`, "_blank")} data-testid="task-action-print">
                  <Printer className="w-4 h-4 mr-2" /> Print
                </DropdownMenuItem>
                {isWebSale && (
                  <DropdownMenuItem data-testid="task-action-signoff"
                    onClick={async () => {
                      if (!window.confirm("Sign off this web sale and move it to Archived?")) return;
                      try {
                        await api.post(`/tasks/${id}/signoff`);
                        toast.success("Web sale signed off · moved to Archived");
                        navigate("/web-sales");
                      } catch (e) { toast.error(e.response?.data?.detail || "Could not sign off"); }
                    }}>
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Sign off web sale
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={downloadMedia} data-testid="task-action-download-media">
                  <Download className="w-4 h-4 mr-2" /> Download Media
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setAddFormOpen(true)} data-testid="task-action-add-form">
                  <FilePlus className="w-4 h-4 mr-2" /> Add Form
                </DropdownMenuItem>
                {user?.is_super && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setDelOpen(true)} data-testid="task-action-delete"
                      className="text-rose-600 focus:text-rose-700 focus:bg-rose-50">
                      <Trash2 className="w-4 h-4 mr-2" /> Delete job
                    </DropdownMenuItem>
                  </>
                )}
            </JobActionsMenu>
          </div>
        </div>

        {/* Task navigation */}
        {!isWebSale && (
        <div className="flex gap-1 mt-4 overflow-x-auto">
          {TABS.map((tb) => (
            <button key={tb} onClick={() => setTab(tb)} data-testid={`task-tab-${tb.toLowerCase()}`}
              className={`px-3 py-1.5 text-sm font-medium rounded-md whitespace-nowrap transition-colors ${tab === tb ? "bg-indigo-50 text-indigo-700" : "text-slate-500 hover:bg-slate-100"}`}>
              {tb}
            </button>
          ))}
        </div>
        )}
      </div>

      {tab === "Map" ? (
        <TaskMapTab id={id} />
      ) : tab === "Finance" ? (
        <TaskFinanceTab task={data.task} finance={data.finance} quotes={quotes} assignmentsProp={data.product_assignments} />
      ) : tab === "More Information" ? (
        <MoreInfoTab legacyId={id} assignments={data.product_assignments} moreInfo={data.more_info} />
      ) : tab === "Highlights" ? (
        <HighlightsTab timeline={data.timeline} />
      ) : tab === "Requirements" ? (
        <div className="p-6 lg:p-8">
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="requirements-tab">
            <h2 className="text-lg font-bold text-slate-900 mb-3">Requirements on this job</h2>
            <JobRequirements shifts={data.shifts} legacyId={id} customerSignoff={data.customer_signoff} onChanged={load} />
          </section>
        </div>
      ) : tab === "Shifts" ? (
        <div className="p-6 lg:p-8">
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="task-shifts-tab">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-bold text-slate-900">Shifts</h2>
              {isAdmin && <Button size="sm" className="gap-1.5 rounded-[8px] h-10 px-4 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setShiftCreate(true)} data-testid="task-create-shift-btn"><Plus className="w-4 h-4" /> Create shift</Button>}
            </div>
            {(!data.shifts || data.shifts.length === 0) ? (
              <EmptyBlock text="No Shifts Found" />
            ) : (
              <div className="space-y-3">
                {data.shifts.map((s) => <ShiftCard key={s.shift_id} shift={s} onOpenFull={s.source === "scheduler" ? () => setShiftSummaryId(s.shift_id) : null} />)}
              </div>
            )}
          </section>
        </div>
      ) : tab !== "Detail" ? (
        <div className="p-6 lg:p-8">
          <div className="max-w-xl mx-auto bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center" data-testid="task-tab-placeholder">
            <span className="mx-auto w-12 h-12 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center mb-4"><Info className="w-6 h-6" /></span>
            <h3 className="text-lg font-semibold text-slate-800">{tab}</h3>
            <p className="text-sm text-slate-500 mt-1">Not included in this demonstration.</p>
          </div>
        </div>
      ) : isWebSale ? (
        <WebSaleDetail id={id} data={data} quotes={quotes} />
      ) : isShopSale ? (
        <ShopSaleDetail id={id} data={data} />
      ) : (
        <div className="p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="task-detail-content">
          {/* Main column */}
          <div className="lg:col-span-2 space-y-6">
            {t.job_summary_status === "REBOOK" && <RebookPanel rebook={t.rebook} evidence={data.rebook_evidence || []} />}
            <JobOverview t={t} endUser={data.end_user} canEdit={isAdmin} onSave={patch} />
            <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="task-shift-summary">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base font-bold text-slate-900">Shifts</h2>
                {isAdmin && <Button size="sm" className="gap-1.5 rounded-[8px] h-10 px-4 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setShiftCreate(true)} data-testid="task-create-shift-btn-detail"><Plus className="w-4 h-4" /> Create shift</Button>}
              </div>
              {(!data.shifts || data.shifts.length === 0) ? (
                <EmptyBlock text="No Shifts Found" />
              ) : (
                <div className="space-y-3">
                  {data.shifts.map((s) => <ShiftCard key={s.shift_id} shift={s} onOpenFull={s.source === "scheduler" ? () => setShiftSummaryId(s.shift_id) : null} />)}
                </div>
              )}
            </section>

            <MoreInfoPanel title="Requirements" icon={ListChecks} testid="task-requirements-card" defaultOpen={!!t.enquiry_details?.answers?.length}>
              <EnquiryDetails details={t.enquiry_details} source={t.enquiry_source} />
              <JobRequirements shifts={data.shifts} legacyId={id} customerSignoff={data.customer_signoff} onChanged={load} />
            </MoreInfoPanel>

            <MoreInfoPanel title="Products" icon={Package} testid="task-products-card">
              <div className="divide-y divide-slate-100 -my-5 [&_section]:border-0 [&_section]:shadow-none [&_section]:rounded-none [&_section]:px-0 [&_section]:py-5">
                <JobPickSection legacyId={id} isAdmin={isAdmin} />
                <JobLoadedItems legacyId={id} />
              </div>
            </MoreInfoPanel>

            <TaskFiles legacyId={id} />

            <TaskTodos legacyId={id} />

            <TaskComments legacyId={id} />

            <MoreInfoPanel title="Finance Summary" icon={PoundSterling} testid="task-finance-summary-card" defaultOpen={false}>
              {(() => {
                const accepted = quotes.filter((q) => !q.superseded && (q.status || "").toLowerCase() === "accepted");
                const latest = {};
                accepted.forEach((q) => { const k = q.quote_number || q.id; if (!latest[k] || (q.version || 0) > (latest[k].version || 0)) latest[k] = q; });
                const earnings = Object.values(latest).reduce((a, q) => a + (q.totals?.total_inc_vat || 0), 0) || data.finance.task_earnings || 0;
                const fmt = (n) => `£${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                return (<>
                  <div className="flex items-center justify-between text-sm border-b border-slate-100 py-2">
                    <span className="text-slate-600">Job Earnings</span>
                    <span className="font-semibold text-slate-900" data-testid="finsum-earnings">{fmt(earnings)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm border-b border-slate-100 py-2">
                    <span className="text-slate-600">Job Costs</span>
                    <span className="font-semibold text-rose-600" data-testid="finsum-costs">{fmt(finCosts)}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm py-2">
                    <span className="text-slate-600">Job Profit</span>
                    <span className={`font-semibold ${earnings - finCosts >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="finsum-profit">{fmt(earnings - finCosts)}</span>
                  </div>
                  <JobPaymentsSummary legacyId={id} fallbackTotal={earnings} compact testid="finsum-payments" />
                </>);
              })()}

              <div className="mt-4 pt-4 border-t border-slate-100" data-testid="task-quotes">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-bold text-slate-900">Quotes ({quotes.length})</h3>
                  <div className="flex items-center gap-3">
                    <Link to={`/quotes?task=${id}`} className="text-xs font-semibold text-indigo-600 hover:underline" data-testid="task-add-quote">+ Add quote</Link>
                    <Link to="/quotes" className="text-xs text-indigo-600 hover:underline">All quotes</Link>
                  </div>
                </div>
                {quotes.length === 0 ? (
                  <EmptyBlock text="No quotes linked to this task." />
                ) : (
                  <div className="space-y-2">
                    {quotes.map((qt) => (
                      <Link key={qt.quote_number} to={`/quotes/${qt.quote_number}`} data-testid={`task-quote-${qt.quote_number}`}
                        className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 hover:bg-slate-50">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-slate-800 font-mono">{qt.quote_number}</div>
                          <div className="text-[11px] text-slate-400">{qt.quote_date} · {qt.status}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-bold text-slate-900">£{(qt.totals.total_inc_vat || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                          <div className="text-[11px] text-indigo-600">Deposit £{(qt.totals.deposit_required || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            </MoreInfoPanel>
          </div>

          {/* Right column */}
          <div className="space-y-6">
            <CustomerPayment legacyId={id} value={t.payment_method} depositDate={t.deposit_date} finalDate={t.final_payment_date} onSaved={load}
              quotes={quotes} onViewAll={() => { setTab("Finance"); window.scrollTo({ top: 0, behavior: "smooth" }); }} />

            <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="task-alerts">
              <h2 className="text-base font-bold text-slate-900 mb-3">Alerts</h2>
              <div className="space-y-2.5">
                {data.alerts.length === 0 && <EmptyBlock text="No alerts" />}
                {data.alerts.map((a) => {
                  const tn = ALERT_TONE[a.tone] || ALERT_TONE.info;
                  return (
                    <div key={a.key} className={`flex gap-2.5 rounded-lg border p-3 ${tn.wrap}`} data-testid={`alert-${a.key}`}>
                      <tn.Icon className={`w-4 h-4 mt-0.5 shrink-0 ${tn.icon}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900">{a.title}</div>
                        <div className="text-xs text-slate-600">{a.detail}</div>
                        {isAdmin && a.key === "no_shift" && (
                          <Button size="sm" className="mt-2 h-8 px-3 rounded-[8px] gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setShiftCreate(true)} data-testid="alert-schedule-shift-btn"><Plus className="w-3.5 h-3.5" /> Schedule a shift</Button>
                        )}
                        {isAdmin && a.key === "awaiting_deposit" && (
                          <Button size="sm" variant="outline" className="mt-2 h-7 gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-100" onClick={recordDeposit} data-testid="alert-record-deposit-btn"><CreditCard className="w-3.5 h-3.5" /> Record deposit</Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            <JobSmsPanel legacyId={id} customerId={data.end_user?.legacy_id} customerName={data.end_user?.full_name} defaultOpen
              autoOpen={new URLSearchParams(window.location.search).get("sms") === "1"} />
          </div>
        </div>
      )}

      <div className="px-6 lg:px-8 pb-8 max-w-[1100px]">
        <SalesIntelCard legacyId={t.legacy_id} taskType={t.task_type} />
      </div>

      <Dialog open={moveOpen} onOpenChange={setMoveOpen}>
        <DialogContent data-testid="task-move-dialog">
          <DialogHeader><DialogTitle>Move job to another department</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-slate-600">Destination department</label>
              <Select value={moveTarget} onValueChange={setMoveTarget}>
                <SelectTrigger className="mt-1" data-testid="move-dest-select"><SelectValue placeholder="Choose department" /></SelectTrigger>
                <SelectContent>
                  {mv.allowed_targets.map((d) => (
                    <SelectItem key={d} value={d} data-testid={`move-dest-${d.toLowerCase()}`}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Assign to (optional)</label>
              <Select value={moveAssignee} onValueChange={setMoveAssignee}>
                <SelectTrigger className="mt-1" data-testid="move-assignee-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {assignees.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name} · {a.email}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-400 mt-1">Only staff permitted in {moveTarget || "the destination"} are listed. Any current assignee outside that department is cleared automatically.</p>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Handover note (optional)</label>
              <textarea value={moveNote} onChange={(e) => setMoveNote(e.target.value)} rows={2} data-testid="move-handover-note"
                placeholder="e.g. Customer has accepted the quotation."
                className="mt-1 w-full rounded-md border border-slate-200 p-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-200" />
            </div>
            {moveTarget && (
              <div className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-3 py-2" data-testid="move-confirm-text">
                Move this job to {moveTarget}? It will no longer be visible to the {DEPT_TEAM_LABEL[t.current_department] || "current team"}.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveOpen(false)}>Cancel</Button>
            <Button className="rounded-[8px] h-10 px-4 bg-indigo-600 hover:bg-indigo-700 text-white" disabled={!moveTarget || moving}
              onClick={doMove} data-testid="move-confirm-btn">
              {moving ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Moving…</> : "Move job"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent data-testid="task-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-700">
              <AlertTriangle className="w-5 h-5" /> Permanently delete this job?
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-slate-600 space-y-2">
            <p>This permanently deletes <span className="font-semibold text-slate-900">{t.legacy_id}: {t.title}</span> and all related records — shifts, media &amp; files, form submissions, product requests, stock movements and planning entries.</p>
            <p className="font-semibold text-rose-600">This cannot be undone.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelOpen(false)} data-testid="task-delete-cancel">Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={deleting}
              onClick={doDelete} data-testid="task-delete-confirm">
              {deleting ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Deleting…</> : "Delete permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {editOpen && (
        <JobForm mode="edit" task={t} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); load(); }} />
      )}
      {addFormOpen && <AddFormPicker legacyId={id} onClose={() => setAddFormOpen(false)} onDone={() => { setAddFormOpen(false); load(); }} />}
      {shiftSummaryId && <ShiftSummaryPanel shiftId={shiftSummaryId} onClose={() => setShiftSummaryId(null)} onReload={load}
        onEdit={(sh) => { setShiftSummaryId(null); setShiftEdit(sh); }}
        onOpenDay={(vehicle, date) => { setShiftSummaryId(null); setDayView({ vehicle, date }); }} />}
      {shiftEdit && <ShiftForm shift={shiftEdit} onClose={() => setShiftEdit(null)} onSaved={() => { setShiftEdit(null); load(); }} />}
      {shiftCreate && <ShiftForm initial={{ task_legacy_id: id }} onClose={() => setShiftCreate(false)} onSaved={() => { setShiftCreate(false); load(); }} />}
      {dayView && <DaySummaryPanel vehicle={dayView.vehicle} day={dayView.date} onClose={() => setDayView(null)} onReload={load} />}
    </div>
  );
}

function JobPickSection({ legacyId, isAdmin }) {
  const picked = useJobPicked(legacyId);
  const flags = {};
  (picked?.conflicts || []).forEach((c) => { if (c.request_id != null) (flags[c.request_id] = flags[c.request_id] || []).push(c.message); });
  return (
    <>
      <TaskProductRequests legacyId={legacyId} isAdmin={isAdmin} flags={flags} />
      <JobPickedItems data={picked} />
    </>
  );
}

function TaskProductRequests({ legacyId, isAdmin, flags = {} }) {
  const [data, setData] = React.useState({ items: [], outstanding: 0 });
  const [adding, setAdding] = React.useState(false);
  const [allocOpen, setAllocOpen] = React.useState(false);
  const load = React.useCallback(() => {
    api.get(`/product-requests/by-task/${legacyId}`).then((r) => setData(r.data || { items: [] })).catch(() => {});
  }, [legacyId]);
  React.useEffect(() => { load(); }, [load]);

  const remove = async (rid) => {
    try { await api.delete(`/product-requests/${rid}`); toast.success("Removed from job"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove"); }
  };
  const approve = async (rid) => {
    try { await api.post(`/product-requests/${rid}/approve`); toast.success("Approved"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not approve"); }
  };
  const approveAll = async () => {
    try { const r = await api.post(`/product-requests/by-task/${legacyId}/approve-all`); toast.success(`Approved ${r.data.approved} product(s)`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not approve all"); }
  };
  const badge = (s) => {
    const map = { Requested: "bg-blue-100 text-blue-700", Approved: "bg-emerald-100 text-emerald-700",
      "Installed - Finance Requested": "bg-amber-100 text-amber-700", Fulfilled: "bg-emerald-100 text-emerald-700",
      Rejected: "bg-rose-100 text-rose-700" };
    return map[s] || "bg-slate-100 text-slate-600";
  };
  const items = data.items || [];
  const pending = items.filter((i) => i.status === "Requested").length;
  const pendingUnits = items.filter((i) => i.status === "Requested").reduce((a, i) => a + (Number(i.qty_required) || 0), 0);

  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="task-product-requests">
      <AllocateProductsDialog legacyId={legacyId} open={allocOpen} onOpenChange={setAllocOpen} />
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><Package className="w-4 h-4" /></span>
          <div>
            <h2 className="text-base font-bold text-slate-900">Product Requests</h2>
            <p className="text-[11px] text-slate-500">{data.outstanding || 0} outstanding · appear automatically when a quote is accepted</p>
          </div>
        </div>
        {isAdmin && (
          <div className="flex items-center gap-2">
            {pending > 0 && (
              <Button size="sm" variant="outline" className="gap-1.5 rounded-[8px] h-10 px-4 border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={approveAll} data-testid="pr-approve-all-btn">
                <CheckCircle2 className="w-4 h-4" /> Approve all ({pendingUnits} item{pendingUnits === 1 ? "" : "s"})
              </Button>
            )}
            <Button size="sm" variant="outline" className="gap-1.5 rounded-[8px] h-10 px-4" onClick={() => setAllocOpen(true)} data-testid="pr-allocate-btn">
              <Truck className="w-4 h-4" /> Allocate products
            </Button>
            <Button size="sm" className="gap-1.5 rounded-[8px] h-10 px-4 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setAdding(true)} data-testid="pr-add-btn">
              <Plus className="w-4 h-4" /> Add product
            </Button>
          </div>
        )}
      </div>
      {items.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400" data-testid="pr-empty">
          No products requested yet. They appear here once a quote is accepted — or add one outside the quote scope.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pr-table">
            <thead><tr className="text-left text-xs text-slate-500 border-b border-slate-200">
              <th className="py-2 pr-3">Product</th><th className="py-2 px-3">Source</th><th className="py-2 px-3">Qty</th>
              <th className="py-2 px-3">Stock</th><th className="py-2 px-3">Status</th><th className="py-2 pl-3"></th>
            </tr></thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.request_id} className="border-b border-slate-100" data-testid={`pr-row-${r.request_id}`}>
                  <td className="py-2.5 pr-3 font-medium text-slate-800">{r.product}
                    {flags[r.request_id] && <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 bg-rose-100 text-rose-700" title={flags[r.request_id].join("\n")} data-testid={`pr-conflict-${r.request_id}`}><AlertTriangle className="w-3 h-3" />Pick conflict</span>}
                    {r.short_pick_open && r.pick_short && <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-semibold rounded-full px-2 py-0.5 bg-rose-600 text-white" title={r.pick_short.reason} data-testid={`pr-short-${r.request_id}`}>Short {Number(r.pick_short.qty_short)}{r.pick_short.reason ? ` · ${r.pick_short.reason}` : ""}</span>}
                    {r.request_type === "Task" && <span className="ml-2 text-[10px] font-semibold rounded-full px-2 py-0.5 bg-slate-100 text-slate-500">Extra</span>}</td>
                  <td className="py-2.5 px-3 text-slate-500">{r.quote_number || (r.request_type === "Task" ? "Manual" : "—")}</td>
                  <td className="py-2.5 px-3 text-slate-600">{r.qty_required}</td>
                  <td className={`py-2.5 px-3 ${r.low_stock ? "text-rose-600 font-semibold" : "text-slate-600"}`}>{r.stock_level}{r.low_stock ? " ⚠" : ""}</td>
                  <td className="py-2.5 px-3"><span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${badge(r.status)}`}>{r.status}</span></td>
                  <td className="py-2.5 pl-3 text-right whitespace-nowrap">
                    {isAdmin && r.status === "Requested" && (
                      <button onClick={() => approve(r.request_id)} className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600" title="Approve" data-testid={`pr-approve-${r.request_id}`}><CheckCircle2 className="w-3.5 h-3.5" /></button>
                    )}
                    {isAdmin && <button onClick={() => remove(r.request_id)} className="p-1.5 rounded hover:bg-rose-50 text-rose-500" title="Remove" data-testid={`pr-remove-${r.request_id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {adding && <AddProductDialog legacyId={legacyId} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </section>
  );
}

function AddProductDialog({ legacyId, onClose, onSaved }) {
  const [q, setQ] = React.useState("");
  const [results, setResults] = React.useState([]);
  const [sel, setSel] = React.useState(null);
  const [custom, setCustom] = React.useState("");
  const [qty, setQty] = React.useState(1);
  const [cost, setCost] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => { api.get("/product-requests/product-search", { params: { q } }).then((r) => setResults(r.data.items || [])).catch(() => {}); }, 200);
    return () => clearTimeout(t);
  }, [q]);
  const save = async () => {
    const product = sel?.title || custom.trim();
    if (!product) { toast.error("Choose or type a product"); return; }
    setBusy(true);
    try {
      await api.post("/product-requests", { task_legacy_id: legacyId, product, product_legacy_id: sel?.legacy_id || null, qty_required: Number(qty) || 1, unit_cost: cost !== "" ? Number(cost) : null, notes: notes || null });
      toast.success("Product added to job"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add"); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="pr-add-dialog">
        <DialogHeader><DialogTitle>Add product (outside quote scope)</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500">Search catalogue</label>
            <Input value={q} onChange={(e) => { setQ(e.target.value); setSel(null); }} placeholder="Type a product name…" data-testid="pr-search" />
            {q && !sel && (
              <div className="mt-1 max-h-40 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="pr-search-results">
                {results.length === 0 ? <div className="p-3 text-xs text-slate-400">No matches — you can type a custom product below.</div> :
                  results.map((p) => (
                    <button key={p.legacy_id} onClick={() => { setSel(p); setCustom(""); if (p.cost_price != null) setCost(String(p.cost_price)); }} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm" data-testid={`pr-option-${p.legacy_id}`}>
                      <div className="font-medium text-slate-800">{p.title}</div>
                      <div className="text-[11px] text-slate-400">{p.category || "Product"} · {p.legacy_id}</div>
                    </button>
                  ))}
              </div>
            )}
          </div>
          {sel ? (
            <div className="flex items-center gap-2 rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm" data-testid="pr-selected">
              <Package className="w-4 h-4 text-indigo-600" /><span className="flex-1 truncate text-indigo-800">{sel.title}</span>
              <button onClick={() => setSel(null)} className="text-indigo-400 hover:text-indigo-700" data-testid="pr-selected-clear"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ) : (
            <div><label className="text-xs font-semibold text-slate-500">…or custom product</label>
              <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="e.g. Bespoke cover" data-testid="pr-custom" /></div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div><label className="text-xs font-semibold text-slate-500">Qty</label><Input type="number" min="1" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="pr-qty" /></div>
            <div><label className="text-xs font-semibold text-slate-500">Unit cost (£)</label><Input type="number" min="0" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0.00" data-testid="pr-cost" /></div>
            <div><label className="text-xs font-semibold text-slate-500">Notes</label><Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" data-testid="pr-notes" /></div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="rounded-[8px] h-10 px-4 bg-indigo-600 hover:bg-indigo-700 text-white" disabled={busy} onClick={save} data-testid="pr-save">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Add product</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function JobRequirements({ shifts, legacyId, customerSignoff, onChanged }) {
  const { user } = useAuth();
  const canManage = user?.is_super || user?.permissions?.includes("forms.view");
  const [forms, setForms] = useState([]);
  const [subs, setSubs] = useState([]);
  const [pick, setPick] = useState(false);
  const [fill, setFill] = useState(null); // { formId, shiftId }
  const [viewSub, setViewSub] = useState(null);
  const [detail, setDetail] = useState(null);
  const [signoffView, setSignoffView] = useState(null);
  const [open, setOpen] = useState({});
  const loadSubs = () => api.get("/submissions", { params: { task: legacyId } }).then((r) => setSubs(r.data.items || [])).catch(() => {});
  useEffect(() => { api.get("/forms").then((r) => setForms(r.data.forms || [])).catch(() => {}); loadSubs(); /* eslint-disable-next-line */ }, [legacyId]);
  // Auto-apply matching Form Rule Groups so inherited forms appear as requirements.
  useEffect(() => {
    api.post(`/tasks/${legacyId}/apply-rules`).then((r) => { if (r.data?.added) onChanged?.(); }).catch(() => {});
    /* eslint-disable-next-line */
  }, [legacyId]);

  const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const nameMatch = (a, b) => { const x = norm(a), y = norm(b); return !!x && !!y && (x === y || x.includes(y) || y.includes(x)); };
  const openView = async (sid) => { try { const r = await api.get(`/submissions/${sid}`); setViewSub(r.data); } catch { toast.error("Could not open submission"); } };
  const openSignoff = async () => {
    if (!customerSignoff) { setSignoffView({ pending: true }); return; }
    setSignoffView({ loading: true });
    try { const r = await api.get(`/tasks/${legacyId}/signoff`); setSignoffView({ data: r.data }); }
    catch { setSignoffView({ data: null, error: true }); }
  };
  const openItem = (it) => {
    if (it.__signoff) { openSignoff(); return; }
    const sub = subs.find((s) => nameMatch(s.form_name, it.label));
    if (sub) { openView(sub.id); return; }
    const form = forms.find((f) => nameMatch(f.name, it.label));
    if (form) { setFill({ formId: form.id, shiftId: it.shift_id, requirement: { section: it.section, label: it.label } }); return; }
    setDetail(it);
  };
  const markReq = async (it, done) => {
    if (!it.shift_id) { toast.error("No shift linked to this requirement"); return; }
    try {
      await api.post(`/shifts/${it.shift_id}/requirements/toggle`, { section: it.section, label: it.label, done });
      toast.success(done ? "Marked complete" : "Marked pending");
      setDetail(null);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update requirement"); }
  };

  const STAGE_LABEL = { pre_site: "Pre Site", on_site: "On Site", post_site: "Post Site" };
  const SURFACE_PRESETS = [
    { key: "both", label: "Desktop + App", value: ["desktop", "app"] },
    { key: "desktop", label: "Desktop only", value: ["desktop"] },
    { key: "app", label: "App only", value: ["app"] },
    { key: "off", label: "Off", value: [] },
  ];
  const surfacesOf = (it) => (Array.isArray(it.surfaces) ? it.surfaces : (it.enabled === false ? [] : ["desktop", "app"]));
  const onDesktop = (it) => !it.form_id || surfacesOf(it).includes("desktop");
  const surfaceLabel = (s) => {
    const d = s.includes("desktop"), a = s.includes("app");
    if (d && a) return "Desktop + App";
    if (d) return "Desktop only";
    if (a) return "App only";
    return "Off";
  };
  const addForm = async (f) => {
    setPick(false);
    try {
      const r = await api.post(`/tasks/${legacyId}/requirement-forms`, { form_id: f.id });
      toast.success(`Added "${f.name}" to ${STAGE_LABEL[r.data.stage] || "requirements"}`);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not add form"); }
  };
  const setSurfaces = async (it, value) => {
    try {
      await api.post(`/tasks/${legacyId}/requirement-forms/surfaces`, { form_id: it.form_id, surfaces: value });
      toast.success(`"${it.label}" → ${surfaceLabel(value)}`);
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update form"); }
  };
  const removeForm = async (it) => {
    try {
      await api.delete(`/tasks/${legacyId}/requirement-forms/${it.form_id}`);
      toast.success("Form removed");
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not remove form"); }
  };

  const SECTIONS = [["pre_site", "Pre Site"], ["on_site", "On Site"], ["post_site", "Post Site"]];
  const isSignoffItem = (it) => {
    const l = (it.label || "").toLowerCase();
    return l.includes("sign off") || l.includes("sign-off") || l.includes("signoff") ||
      (l.includes("customer") && (l.includes("sign") || l.includes("signature")));
  };
  const groups = SECTIONS.map(([key, label]) => {
    let items = [];
    (shifts || []).forEach((s) => {
      (s.requirements?.[key]?.items || []).forEach((it) => items.push({ ...it, shift: s.display_no || s.shift_id, shift_id: s.shift_id, section: key }));
    });
    if (key === "on_site") {
      // Dedicated Customer Sign-off is the single source of truth: drop any legacy rule/checklist
      // sign-off items so it is never duplicated, then append it as the last On Site item.
      items = items.filter((it) => !isSignoffItem(it));
      items.push({
        label: "Customer Sign-off", section: "on_site", shift: null, shift_id: null, __signoff: true,
        done: !!customerSignoff,
        completed_by: customerSignoff?.customer_name || null,
        completed_at: customerSignoff?.signed_at ? formatDateTime(customerSignoff.signed_at) : null,
      });
    }
    const active = items.filter(onDesktop);
    const done = active.filter((it) => it.done).length;
    const lastAt = active.filter((it) => it.done && it.completed_at).map((it) => it.completed_at).sort().pop();
    return { key, label, items, total: active.length, done, complete: active.length > 0 && done === active.length, lastAt };
  }).filter((g) => g.items.length > 0);

  return (
    <div data-testid="job-requirements">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-slate-500">Requirements are the forms attached to this job's shifts. Tap one to view the form and answers.</p>
        <div className="relative">
          <Button size="sm" className="gap-1.5 rounded-[8px] h-10 px-4 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setPick((p) => !p)} data-testid="job-req-add-form"><FilePlus className="w-4 h-4" /> Add form</Button>
          {pick && (
            <div className="absolute right-0 top-10 z-40 w-72 max-h-96 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl py-1" data-testid="job-req-form-picker">
              {forms.length === 0 ? <div className="px-4 py-3 text-sm text-slate-400">No forms available.</div> :
                forms.map((f) => <button key={f.id} onClick={() => addForm(f)} className="block w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid={`job-req-pick-${f.id}`}>{f.name}<span className="block text-[11px] text-slate-400">{f.usage || "On Site / App"} · {f.category || "Uncategorised"}</span></button>)}
            </div>
          )}
        </div>
      </div>
      {groups.length === 0 ? <EmptyBlock text="No Job Requirements Found" /> : (
        <div className="space-y-3" data-testid="job-requirements-groups">
          {groups.map((g) => {
            const isOpen = open[g.key] ?? true;
            return (
              <div key={g.key} className="border border-slate-200 rounded-xl overflow-hidden">
                <button onClick={() => setOpen((o) => ({ ...o, [g.key]: !(o[g.key] ?? true) }))}
                  className="flex w-full items-center justify-between gap-3 px-4 py-3 bg-slate-50/60 hover:bg-slate-50 transition-colors"
                  data-testid={`job-req-section-${g.key}`}>
                  <span className="text-sm font-bold text-slate-900">{g.label}</span>
                  <span className="flex items-center gap-2">
                    {g.complete
                      ? <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-0.5">Completed{g.lastAt ? ` ${g.lastAt}` : ""}</span>
                      : <span className="text-[11px] font-semibold text-slate-500 bg-white border border-slate-200 rounded-full px-2.5 py-0.5">{g.done}/{g.total} done</span>}
                    <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                  </span>
                </button>
                {isOpen && (
                  <ul className="divide-y divide-slate-100 border-t border-slate-100">
                    {g.items.map((it, i) => {
                      const isForm = !!it.form_id;
                      const surf = surfacesOf(it);
                      const isOff = isForm && surf.length === 0;
                      const dim = isForm && !surf.includes("desktop");
                      return (
                      <li key={i} className={`flex items-center ${dim ? "bg-slate-50/70" : ""}`}>
                        <button onClick={() => !dim && openItem(it)} disabled={dim} className={`flex min-w-0 flex-1 items-center gap-3 px-4 py-2.5 text-left transition-colors group ${dim ? "cursor-default" : "hover:bg-slate-50"}`} data-testid={`job-req-item-${g.key}-${i}`}>
                          {it.done ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> : <CircleDot className={`w-4 h-4 shrink-0 ${dim ? "text-slate-200" : "text-slate-300"}`} />}
                          <div className="min-w-0 flex-1">
                            <div className={`text-sm font-medium flex items-center gap-2 ${isOff ? "text-slate-400 line-through" : dim ? "text-slate-400" : "text-slate-800 group-hover:text-indigo-600"}`}>
                              <span className="truncate">{it.label}</span>
                              {isForm && <span className="shrink-0 rounded-full bg-indigo-50 text-indigo-600 border border-indigo-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide">Form</span>}
                            </div>
                            <div className="text-[11px] text-slate-400">{it.__signoff ? "Dedicated Customer Sign-off" : `Shift ${it.shift}`}</div>
                          </div>
                          {!dim && <span className={`text-xs shrink-0 ${it.done ? "text-emerald-600" : "text-slate-400"}`}>{it.done ? (it.completed_by ? `Completed by ${it.completed_by}${it.completed_at ? ` at ${it.completed_at}` : ""}` : "Completed") : "Pending"}</span>}
                          {!isForm && <ChevronRight className="w-4 h-4 text-slate-300 group-hover:text-indigo-500 shrink-0" />}
                        </button>
                        {isForm && canManage && (
                          <div className="flex items-center gap-2 pl-2 pr-4 shrink-0">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:border-indigo-300 transition-colors" data-testid={`job-req-form-surface-${it.form_id}`}>
                                  {surfaceLabel(surf)} <ChevronDown className="w-3 h-3" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-44">
                                {SURFACE_PRESETS.map((p) => (
                                  <DropdownMenuItem key={p.key} onClick={() => setSurfaces(it, p.value)} data-testid={`job-req-surface-${it.form_id}-${p.key}`}>
                                    <span className="flex-1">{p.label}</span>
                                    {surfaceLabel(surf) === p.label && <Check className="w-3.5 h-3.5 text-indigo-600" />}
                                  </DropdownMenuItem>
                                ))}
                              </DropdownMenuContent>
                            </DropdownMenu>
                            <button onClick={() => removeForm(it)} className="text-slate-300 hover:text-rose-500 transition-colors" title="Remove form" data-testid={`job-req-form-remove-${it.form_id}`}><Trash2 className="w-4 h-4" /></button>
                          </div>
                        )}
                      </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
      {fill && <FormFiller formId={fill.formId} context={{ task_legacy_id: legacyId, shift_id: fill.shiftId, requirement_ref: fill.requirement || null }} onClose={() => setFill(null)} onSubmitted={() => { setFill(null); loadSubs(); onChanged?.(); }} />}
      {viewSub && <FormFiller submission={viewSub} context={{ task_legacy_id: legacyId }} mapTaskId={legacyId} onClose={() => setViewSub(null)} />}
      {signoffView && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-500/20 backdrop-blur-[1px]" onClick={() => setSignoffView(null)} data-testid="job-signoff-panel">
          <div className="relative h-full w-full max-w-md bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <h3 className="text-lg font-bold text-slate-900">Customer Sign-off</h3>
              <button onClick={() => setSignoffView(null)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100" data-testid="job-signoff-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {signoffView.pending ? (
                <>
                  <div className="flex items-center gap-2"><CircleDot className="w-5 h-5 text-slate-300" /><span className="text-sm font-semibold text-slate-500" data-testid="job-signoff-status">Pending</span></div>
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-500">No customer sign-off captured yet. This is captured on-site via the engineer’s <span className="font-medium text-slate-700">Customer Sign-off</span> screen in TedOps Field.</div>
                </>
              ) : signoffView.loading ? (
                <div className="text-sm text-slate-400">Loading sign-off…</div>
              ) : signoffView.data ? (
                <>
                  <div className="flex items-center gap-2"><CheckCircle2 className="w-5 h-5 text-emerald-500" /><span className="text-sm font-semibold text-emerald-600" data-testid="job-signoff-status">Signed</span></div>
                  <div><div className="text-xs uppercase tracking-wide text-slate-400">Customer</div><div className="text-sm font-medium text-slate-800" data-testid="job-signoff-customer">{signoffView.data.customer_name || "—"}</div></div>
                  {signoffView.data.signed_at && <div><div className="text-xs uppercase tracking-wide text-slate-400">Signed</div><div className="text-sm text-slate-700">{formatDateTime(signoffView.data.signed_at)}</div></div>}
                  {signoffView.data.captured_by_name && <div><div className="text-xs uppercase tracking-wide text-slate-400">Captured by</div><div className="text-sm text-slate-700">{signoffView.data.captured_by_name}</div></div>}
                  {signoffView.data.comments && <div><div className="text-xs uppercase tracking-wide text-slate-400">Comments</div><div className="text-sm text-slate-700 whitespace-pre-wrap">{signoffView.data.comments}</div></div>}
                  <div><div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Signature</div><SignaturePad value={signoffView.data.signature} disabled /></div>
                </>
              ) : (
                <div className="text-sm text-rose-600">Could not load the sign-off.</div>
              )}
            </div>
          </div>
        </div>
      )}
      {detail && (
        <div className="fixed inset-0 z-50 flex justify-end bg-slate-500/20 backdrop-blur-[1px]" onClick={() => setDetail(null)} data-testid="job-req-detail-panel">
          <div className="relative h-full w-full max-w-md bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
              <h3 className="text-lg font-bold text-slate-900">{detail.label}</h3>
              <button onClick={() => setDetail(null)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100" data-testid="job-req-detail-close"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                {detail.done ? <CheckCircle2 className="w-5 h-5 text-emerald-500" /> : <CircleDot className="w-5 h-5 text-slate-300" />}
                <span className={`text-sm font-semibold ${detail.done ? "text-emerald-600" : "text-slate-500"}`} data-testid="job-req-detail-status">{detail.done ? "Completed" : "Pending"}</span>
              </div>
              <div className="text-xs uppercase tracking-wide text-slate-400">Shift {detail.shift}</div>
              {detail.done
                ? <div className="text-sm text-slate-600">Completed by {detail.completed_by || "—"}{detail.completed_at ? ` at ${detail.completed_at}` : ""}</div>
                : <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-sm text-slate-500">This check has no attached form. You can complete it here as a manager, or it’s captured on-site via the engineer’s app.</div>}
              {canManage && (
                detail.done
                  ? <Button variant="outline" className="w-full gap-1.5 border-slate-300 text-slate-600 hover:bg-slate-50" onClick={() => markReq(detail, false)} data-testid="job-req-mark-pending"><CircleDot className="w-4 h-4" /> Mark as pending</Button>
                  : <Button className="w-full gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => markReq(detail, true)} data-testid="job-req-mark-complete"><CheckCircle2 className="w-4 h-4" /> Mark complete</Button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

async function downloadJobMedia(legacyId) {
  const [m, f] = await Promise.all([
    api.get(`/tasks/${legacyId}/all-media`).then((r) => r.data.items || []).catch(() => []),
    api.get(`/tasks/${legacyId}/files`).then((r) => r.data.items || []).catch(() => []),
  ]);
  if (!m.length && !f.length) { toast.info("No media on this job yet"); return; }
  toast.info(`Downloading ${m.length + f.length} file(s)…`);
  let failed = 0;
  const save = (url, name) => { const a = document.createElement("a"); a.href = url; a.download = name || "file"; a.rel = "noopener"; document.body.appendChild(a); a.click(); a.remove(); };
  for (const x of m) {
    try { const r = await api.get(`/attachments/${x.id}/view`, { params: { download: true } }); save(r.data.url, x.filename); }
    catch { failed += 1; }
    await new Promise((res) => setTimeout(res, 400));
  }
  for (const x of f) {
    try { const r = await api.get(`/files/${x.path}`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); save(u, x.filename); setTimeout(() => URL.revokeObjectURL(u), 5000); }
    catch { failed += 1; }
    await new Promise((res) => setTimeout(res, 400));
  }
  if (failed) toast.error(`${failed} file(s) could not be downloaded (still scanning or unavailable)`);
}

function AddFormPicker({ legacyId, onClose, onDone }) {
  const [forms, setForms] = useState(null);
  const [fillId, setFillId] = useState(null);
  useEffect(() => { api.get("/forms").then((r) => setForms(r.data.forms || [])).catch(() => setForms([])); }, []);
  if (fillId) return <FormFiller formId={fillId} context={{ task_legacy_id: legacyId }} onClose={onClose} onSubmitted={onDone} />;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="add-form-dialog">
        <DialogHeader><DialogTitle>Add a form to this job</DialogTitle></DialogHeader>
        <div className="max-h-96 overflow-y-auto -mx-2">
          {!forms ? <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-slate-400" /></div>
            : forms.length === 0 ? <div className="p-4 text-sm text-slate-400">No forms available.</div>
            : forms.map((f) => (
              <button key={f.id} onClick={() => setFillId(f.id)} className="block w-full text-left px-3 py-2 rounded-[8px] text-sm text-slate-700 hover:bg-slate-50" data-testid={`add-form-pick-${f.id}`}>
                {f.name}<span className="block text-[11px] text-slate-400">{f.category || "Uncategorised"}</span>
              </button>
            ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function JobFormsTab({ legacyId }) {
  const [subs, setSubs] = useState(null);
  const [forms, setForms] = useState([]);
  const [pick, setPick] = useState(false);
  const [fillId, setFillId] = useState(null);
  const [view, setView] = useState(null);
  const loadSubs = () => api.get("/submissions", { params: { task: legacyId } }).then((r) => setSubs(r.data.items)).catch(() => setSubs([]));
  useEffect(() => { loadSubs(); api.get("/forms").then((r) => setForms(r.data.forms || [])).catch(() => {}); /* eslint-disable-next-line */ }, [legacyId]);
  const openView = async (sid) => { try { const r = await api.get(`/submissions/${sid}`); setView(r.data); } catch { toast.error("Could not open"); } };
  const del = async (sid) => { if (!window.confirm("Delete this submission?")) return; try { await api.delete(`/submissions/${sid}`); loadSubs(); } catch (e) { toast.error(e.response?.data?.detail || "Could not delete"); } };
  return (
    <div className="p-6 lg:p-8" data-testid="job-forms-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-slate-900">Requirements on this job</h2>
        <div className="relative">
          <Button className="gap-1.5 rounded-[8px] h-10 px-4 bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => setPick((p) => !p)} data-testid="job-fill-form-btn"><FilePlus className="w-4 h-4" /> Fill a form</Button>
          {pick && (
            <div className="absolute right-0 top-11 z-40 w-72 max-h-96 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-xl py-1" data-testid="job-form-picker">
              {forms.length === 0 ? <div className="px-4 py-3 text-sm text-slate-400">No forms available.</div> :
                forms.map((f) => <button key={f.id} onClick={() => { setPick(false); setFillId(f.id); }} className="block w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50" data-testid={`job-pick-form-${f.id}`}>{f.name}<span className="block text-[11px] text-slate-400">{f.category || "Uncategorised"}</span></button>)}
            </div>
          )}
        </div>
      </div>
      {!subs ? <div className="p-8 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div> :
        subs.length === 0 ? <div className="bg-white border border-dashed border-slate-300 rounded-2xl p-10 text-center text-sm text-slate-400" data-testid="job-forms-empty">No forms submitted for this job yet.</div> : (
          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm divide-y divide-slate-100">
            {subs.map((sub) => (
              <div key={sub.id} className="flex items-center gap-3 px-5 py-3" data-testid={`job-sub-${sub.id}`}>
                <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
                <button onClick={() => openView(sub.id)} className="min-w-0 flex-1 text-left">
                  <div className="text-sm font-semibold text-slate-800">{sub.form_name}</div>
                  <div className="text-[11px] text-slate-400">by {sub.submitted_by} · {sub.submitted_at ? new Date(sub.submitted_at).toLocaleString("en-GB") : ""}</div>
                </button>
                <button onClick={() => del(sub.id)} className="text-slate-300 hover:text-rose-500 shrink-0" data-testid={`job-sub-del-${sub.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      {fillId && <FormFiller formId={fillId} context={{ task_legacy_id: legacyId }} onClose={() => setFillId(null)} onSubmitted={() => { setFillId(null); loadSubs(); }} />}
      {view && <FormFiller submission={view} context={{ task_legacy_id: legacyId }} mapTaskId={legacyId} onClose={() => setView(null)} />}
    </div>
  );
}

function fmtDateTime(ts) {
  if (!ts) return "";
  try {
    const d = new Date(ts);
    const date = d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
    const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
    return `${date} at ${time}`;
  } catch {
    return ts;
  }
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-0.5">{children}</div>
    </div>
  );
}
function QG({ label, value }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-800 font-medium text-right">{value || "—"}</span>
    </div>
  );
}

const fmtDur = (m) => {
  if (m == null || Number.isNaN(Number(m))) return "—";
  const mins = Math.round(Number(m));
  const h = Math.floor(mins / 60);
  const mm = mins % 60;
  if (h <= 0) return `${mm} min`;
  return mm ? `${h}hr ${mm}min` : `${h}hr`;
};


function MapStat({ label, value, tone }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3">
      <div className="text-[10px] uppercase font-semibold text-slate-400">{label}</div>
      <div className={`text-lg font-bold ${tone || "text-slate-900"}`}>{value}</div>
    </div>
  );
}

const gbp2 = (v) => `£${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function FinTotalRow({ label, value, tone, bold, top, sub }) {
  return (
    <div className={`flex items-start justify-between px-5 py-2 text-sm ${top ? "border-t border-slate-200 pt-3 mt-1" : ""}`}>
      <span className={bold ? "font-bold text-slate-900" : "text-slate-600"}>
        {label}
        {sub && <span className="block text-[11px] font-normal text-slate-400 mt-0.5" data-testid="finance-revenue-source">{sub}</span>}
      </span>
      <span className={`${bold ? "font-bold" : "font-semibold"} ${tone || "text-slate-900"}`}>{value}</span>
    </div>
  );
}

function WSCard({ title, icon: Icon, children, testid }) {
  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        {Icon && <span className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center"><Icon className="w-4 h-4" /></span>}
        <h2 className="text-base font-bold text-slate-900">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function WebSaleDetail({ id, data, quotes }) {
  const t = data.task;
  const [ful, setFul] = useState(null);
  useEffect(() => {
    api.get(`/shipping/resolve/${id}`).then((r) => setFul(r.data.fulfilment || null)).catch(() => setFul(null));
  }, [id]);
  const addrLines = ful ? [ful.address_1, ful.address_2, ful.city, ful.county, ful.postcode, ful.country].filter(Boolean) : [];

  return (
    <div className="p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-testid="web-sale-detail">
      <div className="lg:col-span-2 space-y-6">
        {/* Everything from the Finance tab, surfaced for web sales */}
        <TaskFinanceTab task={data.task} finance={data.finance} quotes={quotes} webSale assignmentsProp={data.product_assignments} />
        <TaskFiles legacyId={id} />
      </div>
        <div className="space-y-6">
          <WSCard title="Customer & delivery" icon={User} testid="ws-customer">
            {ful ? (
              <div className="space-y-3 text-sm">
                <QG label="Recipient" value={ful.recipient_name} />
                {ful.company && <QG label="Company" value={ful.company} />}
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Delivery address</div>
                  <div className="text-slate-800">
                    {addrLines.length ? addrLines.map((ln, i) => <div key={i}>{ln}</div>) : <span className="text-slate-400">—</span>}
                  </div>
                </div>
                <QG label="Email" value={ful.email} />
                <QG label="Phone" value={ful.phone} />
              </div>
            ) : <EmptyBlock text="No delivery details on this order." />}
          </WSCard>

          <WSCard title="Order timeline" icon={Clock} testid="ws-timeline">
            {(!data.timeline || data.timeline.length === 0) ? <EmptyBlock text="No timeline activity yet." /> : (
              <ol className="relative border-l border-slate-200 ml-2 space-y-4">
                {data.timeline.map((e, i) => (
                  <li key={i} className="ml-4">
                    <span className="absolute -left-[6.5px] w-3 h-3 rounded-full bg-indigo-400 border-2 border-white" />
                    <div className="text-sm font-semibold text-slate-900">{e.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{e.detail}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5">{e.ts}</div>
                  </li>
                ))}
              </ol>
            )}
          </WSCard>
          <TaskComments legacyId={id} />
          <JobSmsPanel legacyId={id} customerId={data.end_user?.legacy_id} customerName={data.end_user?.full_name} />
        </div>
    </div>
  );
}

function ShopSaleDetail({ id, data }) {
  const t = data.task;
  const fin = t.finance || {};
  const items = t.line_items || [];
  const money = (n) => `£${(Number(n) || 0).toFixed(2)}`;
  return (
    <div className="p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6 items-start" data-testid="shop-sale-detail">
      <div className="lg:col-span-2 space-y-6">
        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="shop-sale-invoice">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <div className="flex items-center gap-2 text-base font-bold text-slate-900"><Receipt className="w-4 h-4 text-emerald-600" /> Shop Sale</div>
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700">{fin.reference || t.legacy_id}</span>
          </div>
          {items.length ? (
            <table className="w-full text-sm" data-testid="shop-sale-items">
              <thead><tr className="bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400">
                <th className="px-5 py-2">Product</th><th className="px-3 py-2 w-24">SKU</th>
                <th className="px-3 py-2 w-14 text-right">Qty</th><th className="px-3 py-2 w-24 text-right">Unit</th>
                <th className="px-3 py-2 w-16 text-right">VAT</th><th className="px-5 py-2 w-24 text-right">Total</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((li, i) => (
                  <tr key={i} data-testid={`shop-sale-item-${i}`}>
                    <td className="px-5 py-3 text-slate-800 font-medium">{li.title || "Item"}</td>
                    <td className="px-3 py-3 text-slate-500 font-mono">{li.sku || "—"}</td>
                    <td className="px-3 py-3 text-slate-600 text-right">{li.qty}</td>
                    <td className="px-3 py-3 text-slate-600 text-right">{money(li.unit_price)}</td>
                    <td className="px-3 py-3 text-slate-500 text-right">{li.vat_rate != null ? `${+((li.vat_rate <= 1 ? li.vat_rate * 100 : li.vat_rate)).toFixed(2)}%` : "—"}</td>
                    <td className="px-5 py-3 text-slate-900 font-semibold text-right">{money(li.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <div className="p-5"><EmptyBlock text="No line items recorded for this sale." /></div>}
          <div className="px-5 py-4 border-t border-slate-100 flex justify-end">
            <div className="w-full max-w-xs text-sm space-y-1.5">
              <div className="flex justify-between text-slate-500"><span>Net</span><span>{money(fin.net)}</span></div>
              {fin.discount?.amount > 0 && <div className="flex justify-between text-rose-600"><span>Discount</span><span>-{money(fin.discount.amount)}</span></div>}
              <div className="flex justify-between text-slate-500"><span>VAT</span><span>{money(fin.vat)}</span></div>
              <div className="flex justify-between pt-2 border-t border-slate-200 text-lg font-bold text-slate-900"><span>Total</span><span data-testid="shop-sale-total">{money(fin.gross ?? t.earnings)}</span></div>
            </div>
          </div>
        </div>
        <TaskFiles legacyId={id} />
        <TaskComments legacyId={id} />
      </div>
      <div className="space-y-6">
        <WSCard title="Customer" icon={User} testid="shop-customer">
          {(data.end_user || t.public_contact) ? (
            <div className="space-y-3 text-sm">
              <QG label="Name" value={data.end_user?.full_name || t.public_contact} />
              <QG label="Email" value={data.end_user?.email} />
              <QG label="Phone" value={data.end_user?.telephone || data.end_user?.mobile} />
            </div>
          ) : <EmptyBlock text="Walk-in customer — no details captured." />}
        </WSCard>
        <WSCard title="Sale details" icon={Receipt} testid="shop-sale-meta">
          <div className="space-y-3 text-sm">
            <QG label="Reference" value={fin.reference || t.legacy_id} />
            <QG label="Sold by" value={t.sold_by || "—"} />
            <QG label="Payment method" value={(fin.payment_method || "—").toUpperCase()} />
            <QG label="Depot" value={t.depot} />
            <QG label="Sold" value={(t.created_at || "").slice(0, 16).replace("T", " ")} />
            <QG label="Status" value={t.job_summary_status || t.status} />
          </div>
        </WSCard>
        <RefundVoidPanel task={t} />
        <WSCard title="Timeline" icon={Clock} testid="shop-timeline">
          {(!data.timeline || data.timeline.length === 0) ? <EmptyBlock text="No timeline activity yet." /> : (
            <ol className="relative border-l border-slate-200 ml-2 space-y-4">
              {data.timeline.map((e, i) => (
                <li key={i} className="ml-4">
                  <span className="absolute -left-[6.5px] w-3 h-3 rounded-full bg-emerald-400 border-2 border-white" />
                  <div className="text-sm font-semibold text-slate-900">{e.title}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{e.detail}</div>
                  <div className="text-[11px] text-slate-400 mt-0.5">{e.ts}</div>
                </li>
              ))}
            </ol>
          )}
        </WSCard>
      </div>
    </div>
  );
}

function RefundVoidPanel({ task }) {
  const { user } = useAuth();
  const tid = task.epos_transaction_id;
  const canRefund = user?.is_super || (user?.permissions || []).includes("epos.refund");
  const [txn, setTxn] = useState(null);
  const [refunds, setRefunds] = useState([]);
  const [dlg, setDlg] = useState(null);
  const [reason, setReason] = useState("");
  const [amount, setAmount] = useState("");
  const [rtype, setRtype] = useState("full");
  const [busy, setBusy] = useState(false);
  const money = (n) => `£${(Number(n) || 0).toFixed(2)}`;

  const load = async () => {
    if (!tid) return;
    try {
      const [a, b] = await Promise.all([
        api.get(`/epos/transactions/${tid}`),
        api.get(`/epos/transactions/${tid}/refunds`),
      ]);
      setTxn(a.data.transaction); setRefunds(b.data.refunds || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { load(); }, [tid]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!tid) return null;

  const status = txn?.status || "completed";
  const pillColour = { completed: "emerald", partially_refunded: "amber", refunded: "rose",
    voided: "slate", payment_declined: "rose", cancelled: "slate" }[status] || "slate";
  const canAct = canRefund && ["completed", "partially_refunded"].includes(status);
  const canVoid = canRefund && status === "completed" && !(txn?.refunded_total > 0);

  const submitRefund = async () => {
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try {
      const body = { type: rtype, reason };
      if (rtype === "amount") body.amount = parseFloat(amount || "0");
      await api.post(`/epos/transactions/${tid}/refund`, body);
      toast.success("Refund recorded"); setDlg(null); setReason(""); setAmount(""); setRtype("full"); load();
    } catch (e) { toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Refund failed"); }
    finally { setBusy(false); }
  };
  const submitVoid = async () => {
    if (!reason.trim()) { toast.error("A reason is required"); return; }
    setBusy(true);
    try {
      await api.post(`/epos/transactions/${tid}/void`, { reason });
      toast.success("Transaction voided"); setDlg(null); setReason(""); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Void failed"); }
    finally { setBusy(false); }
  };

  return (
    <WSCard title="Payment & refunds" icon={PoundSterling} testid="shop-refunds">
      <div className="space-y-3 text-sm">
        <div className="flex items-center justify-between">
          <span className="text-slate-500">Status</span>
          <Pill colour={pillColour}><span data-testid="shop-txn-status">{status.replace(/_/g, " ")}</span></Pill>
        </div>
        {txn?.refunded_total > 0 && (
          <div className="flex items-center justify-between"><span className="text-slate-500">Refunded</span>
            <span className="font-semibold text-rose-600" data-testid="shop-refunded-total">{money(txn.refunded_total)}</span></div>
        )}
        {refunds.length > 0 && (
          <div className="space-y-1.5 pt-1">
            {refunds.map((r) => (
              <div key={r.id} className="flex items-center justify-between gap-2 text-xs bg-slate-50 rounded-lg px-2.5 py-1.5" data-testid={`shop-refund-${r.refund_ref}`}>
                <span className="font-mono text-slate-600">{r.refund_ref}</span>
                <span className="text-slate-500 truncate flex-1" title={r.reason}>{r.reason}</span>
                <span className="font-semibold text-rose-600">-{money(r.gross)}</span>
              </div>
            ))}
          </div>
        )}
        {canRefund && (canAct || canVoid) && (
          <div className="flex gap-2 pt-1">
            {canAct && <Button size="sm" variant="outline" className="h-8 text-xs flex-1" onClick={() => { setReason(""); setDlg("refund"); }} data-testid="shop-refund-btn">Refund</Button>}
            {canVoid && <Button size="sm" variant="outline" className="h-8 text-xs flex-1 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => { setReason(""); setDlg("void"); }} data-testid="shop-void-btn">Void</Button>}
          </div>
        )}
        {!canRefund && <p className="text-xs text-slate-400">You do not have permission to refund or void.</p>}
      </div>

      <Dialog open={dlg === "refund"} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="shop-refund-dialog">
          <DialogHeader><DialogTitle>Refund {txn?.ref}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={rtype} onValueChange={setRtype}>
              <SelectTrigger data-testid="shop-refund-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="full">Full refund</SelectItem>
                <SelectItem value="amount">Amount</SelectItem>
              </SelectContent>
            </Select>
            {rtype === "amount" && <Input type="number" step="0.01" placeholder="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="shop-refund-amount" />}
            <Input placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="shop-refund-reason" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button onClick={submitRefund} disabled={busy} data-testid="shop-refund-submit">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Refund</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dlg === "void"} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent data-testid="shop-void-dialog">
          <DialogHeader><DialogTitle>Void {txn?.ref}</DialogTitle></DialogHeader>
          <Input placeholder="Reason (required)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="shop-void-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancel</Button>
            <Button onClick={submitVoid} disabled={busy} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="shop-void-submit">{busy && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Void</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </WSCard>
  );
}

function TaskFinanceTab({ task, finance, quotes, webSale, assignmentsProp }) {
  const { user, can } = useAuth();
  const isAdmin = user?.role === "admin";
  const wf = task?.wc_finance;
  const revDate = (task?.deposit_date || task?.created_at || "").slice(0, 10);
  const [assignments, setAssignments] = useState(assignmentsProp || task?.product_assignments || []);
  const [busy, setBusy] = useState(null);
  const [busyAll, setBusyAll] = useState(false);
  const canManageCosts = can("orders.manage");
  const [costs, setCosts] = useState([]);
  const [costForm, setCostForm] = useState(null);
  const canFinance = can("finance.view");
  const [rollup, setRollup] = useState(null);

  useEffect(() => {
    api.get(`/tasks/${task.legacy_id}/costs`).then((r) => setCosts(r.data.items || [])).catch(() => setCosts([]));
  }, [task.legacy_id]);

  const costsTotal = costs.reduce((a, c) => a + (c.total || 0), 0);
  const onCostSaved = (cost) => {
    setCosts((cs) => { const i = cs.findIndex((x) => x.id === cost.id); if (i >= 0) { const n = [...cs]; n[i] = cost; return n; } return [...cs, cost]; });
    setCostForm(null);
  };
  const delCost = async (cid) => {
    try { await api.delete(`/costs/${cid}`); setCosts((cs) => cs.filter((c) => c.id !== cid)); toast.success("Cost removed"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove cost"); }
  };

  const fulfil = async (index, fulfilled) => {
    setBusy(index);
    try {
      const r = await api.post(`/tasks/${task.legacy_id}/fulfil-line`, { index, fulfilled }, { timeout: 30000 });
      setAssignments((prev) => prev.map((a, i) => (i === index ? r.data.assignment : a)));
      toast.success(r.data.message);
    } catch (e) {
      toast.error(e.code === "ECONNABORTED" ? "Fulfil timed out — please refresh and check the line status" : (e.response?.data?.detail || "Could not update fulfilment"));
    } finally {
      setBusy(null);
    }
  };

  const fulfilAll = async () => {
    const pending = assignments.map((a, i) => (!a.consumed ? i : -1)).filter((i) => i >= 0);
    if (!pending.length) return;
    setBusyAll(true);
    let deducted = 0;
    try {
      for (const index of pending) {
        const r = await api.post(`/tasks/${task.legacy_id}/fulfil-line`, { index, fulfilled: true }, { timeout: 30000 });
        setAssignments((prev) => prev.map((a, i) => (i === index ? r.data.assignment : a)));
        if (r.data.stock_changed) deducted += 1;
      }
      toast.success(`Fulfilled ${pending.length} line${pending.length === 1 ? "" : "s"}${deducted ? ` · stock deducted on ${deducted}` : ""}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not fulfil all lines");
    } finally {
      setBusyAll(false);
    }
  };

  const downloadInvoice = async () => {
    try { await downloadTaskInvoicePdf(task.legacy_id); }
    catch { toast.error("Could not generate invoice PDF"); }
  };

  const downloadPacking = async () => {
    try { await downloadTaskPackingSlip(task.legacy_id); }
    catch { toast.error("Could not generate packing slip"); }
  };

  const [labelReady, setLabelReady] = useState(false);
  const [labelBusy, setLabelBusy] = useState(false);
  useEffect(() => {
    if (!webSale) return;
    api.get(`/royalmail/orders/${task.legacy_id}`)
      .then((r) => setLabelReady(r.data?.shipment?.label_status === "stored"))
      .catch(() => {});
  }, [webSale, task.legacy_id]);
  const printShippingLabel = async () => {
    setLabelBusy(true);
    try {
      const r = await api.get(`/royalmail/orders/${task.legacy_id}/label.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const w = window.open(url, "_blank");
      if (w) w.addEventListener("load", () => { try { w.print(); } catch (_) {} });
    } catch (e) { toast.error(e.response?.data?.detail || "Label not available yet"); }
    finally { setLabelBusy(false); }
  };

  const rows = [];
  let netSum = 0, vatSum = 0;
  if (wf) {
    (wf.lines || []).forEach((l) => {
      const net = l.total, vat = l.total_tax;
      const rate = net > 0 ? Math.round((vat / net) * 100) : 0;
      netSum += net; vatSum += vat;
      rows.push({ title: l.name, sku: l.sku, qty: l.quantity, amount: net, rate, vatTotal: vat, sub: net, total: net + vat, account: "Web Sales", depot: "HQ" });
    });
    const sTotal = wf.shipping_total || 0, sTax = wf.shipping_tax || 0;
    const sRate = sTotal > 0 ? Math.round((sTax / sTotal) * 100) : 0;
    netSum += sTotal; vatSum += sTax;
    rows.push({ title: "Shipping", qty: 1, amount: sTotal, rate: sRate, vatTotal: sTax, sub: sTotal, total: sTotal + sTax, account: "Courier", depot: "HQ" });
  }
  const taskEarnings = wf ? netSum : (finance?.task_earnings || 0);
  const grandTotal = wf ? (wf.total || netSum + vatSum) : taskEarnings;
  const approvedQuoteTotal = quotes.filter((q) => !q.superseded).reduce((a, q) => a + (q.totals?.total_inc_vat || 0), 0);
  const acceptedTotal = quotes.filter((q) => !q.superseded && (q.status || "").toLowerCase() === "accepted").reduce((a, q) => a + (q.totals?.total_inc_vat || 0), 0);
  const jobRevenue = taskEarnings || acceptedTotal;
  const acceptedQuoteNos = quotes.filter((q) => !q.superseded && (q.status || "").toLowerCase() === "accepted").map((q) => q.quote_number);
  const revenueSource = wf
    ? `From web order ${task?.reference || task?.legacy_id || ""}`.trim()
    : acceptedQuoteNos.length
      ? `From accepted quote ${acceptedQuoteNos.join(", ")}`
      : (finance?.task_earnings ? "From recorded job earnings" : null);
  const grossMargin = jobRevenue > 0 ? (((jobRevenue - costsTotal) / jobRevenue) * 100) : null;

  return (
    <div className={webSale ? "space-y-6" : "p-6 lg:p-8 space-y-5"} data-testid="task-finance-tab">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Finance</h1>
        {webSale && (
          <div className="flex items-center gap-2">
            <Button variant="outline" disabled={!labelReady || labelBusy} onClick={printShippingLabel}
              className="h-9 gap-1.5" title={labelReady ? "Print the stored Royal Mail label" : "Available once a Royal Mail label has been pulled below"} data-testid="finance-shipping-label-btn">
              {labelBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Truck className="w-4 h-4" />} Print Shipping Label {!labelReady && <Lock className="w-3 h-3 text-slate-400" />}
            </Button>
            <Button variant="outline" className="h-9 gap-1.5" onClick={downloadPacking} data-testid="finance-packing-btn">
              <Package className="w-4 h-4" /> Packing Slip
            </Button>
            <Button variant="outline" className="h-9 gap-1.5" onClick={downloadInvoice} data-testid="finance-invoice-btn">
              <Download className="w-4 h-4" /> Invoice PDF
            </Button>
          </div>
        )}
      </div>

      {wf && <ShippingResolutionCard legacyId={task.legacy_id} />}

      {!webSale && (<>
      {/* Quotes */}
      <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <div className="flex items-center justify-between"><h2 className="text-sm font-bold text-slate-900">Quotes</h2><Link to={`/quotes?task=${task.legacy_id}`} className="text-xs font-semibold text-indigo-600 hover:underline" data-testid="finance-add-quote">+ Add quote</Link></div>
          {quotes.length > 0 && <span className="text-sm font-semibold text-indigo-700">{gbp2(approvedQuoteTotal)}</span>}
        </div>
        {quotes.length === 0 ? <div className="text-center text-sm text-slate-400 py-6">No Quotes Found</div> : (
          <div className="divide-y divide-slate-100">
            {quotes.map((q) => {
              const qCosts = costs.filter((c) => c.quote_number === q.quote_number && !q.superseded);
              return (
              <div key={`${q.quote_number}-v${q.version || 1}`}>
                <div className={`flex items-center justify-between px-5 py-3 text-sm ${q.superseded ? "opacity-60 bg-slate-50/60" : "hover:bg-slate-50"}`}>
                  <Link to={`/quotes/${q.quote_number}`} className="flex items-center gap-3 flex-1 min-w-0">
                    <span className={`font-mono font-semibold ${q.superseded ? "text-slate-500 line-through decoration-slate-300" : "text-indigo-700"}`}>{q.quote_number}{(q.version > 1 || q.superseded) ? ` v${q.version}` : ""}</span>
                    <span className="text-slate-600 flex-1 px-2 truncate">Quote — {task?.title}</span>
                    {q.superseded
                      ? <span data-testid={`quote-superseded-${q.quote_number}-v${q.version}`}><Pill colour="amber">Superseded</Pill></span>
                      : <Pill colour={{ Draft: "slate", Sent: "blue", Accepted: "green", Declined: "red" }[q.status] || "slate"}>{q.status}</Pill>}
                    <span className={`font-semibold w-24 text-right ${q.superseded ? "text-slate-400 line-through decoration-slate-300" : "text-slate-900"}`}>{gbp2(q.totals?.total_inc_vat)}</span>
                  </Link>
                  {canManageCosts && !q.superseded && (
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1 ml-3 shrink-0" onClick={() => setCostForm({ quoteNumber: q.quote_number })} data-testid={`quote-add-cost-${q.quote_number}`}>
                      <Plus className="w-3.5 h-3.5" /> Cost
                    </Button>
                  )}
                </div>
                {qCosts.length > 0 && (
                  <div className="pl-8 pr-5 pb-2 space-y-1">
                    {qCosts.map((c) => (
                      <button key={c.id} onClick={() => setCostForm({ cost: c })} data-testid={`quote-cost-${c.id}`}
                        className="w-full flex items-center justify-between text-left text-xs rounded-md border border-slate-100 bg-slate-50 hover:bg-indigo-50 hover:border-indigo-200 transition-colors px-3 py-1.5">
                        <span className="truncate text-slate-600 flex items-center gap-1.5"><Receipt className="w-3 h-3 text-slate-400" /> {c.title}</span>
                        <span className="font-semibold text-slate-800 ml-3 shrink-0">{gbp2(c.total)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );})}
          </div>
        )}
      </section>

      {/* Invoices (shown directly below Quotes) */}
      {!webSale && <TaskInvoices legacyId={task.legacy_id} task={task} quotes={quotes} />}

      {/* Costs */}
      <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <h2 className="text-sm font-bold text-slate-900">Costs</h2>
          <div className="flex items-center gap-3">
            {costsTotal > 0 && <span className="text-sm font-semibold text-slate-800">{gbp2(costsTotal)}</span>}
            {canManageCosts && (
              <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => setCostForm({ quoteNumber: null })} data-testid="task-add-cost">
                <Plus className="w-3.5 h-3.5" /> Add
              </Button>
            )}
          </div>
        </div>
        {costs.length === 0 ? (
          <div className="text-center text-sm text-slate-400 py-6">No Costs Found</div>
        ) : (
          <div className="divide-y divide-slate-100">
            {costs.map((c) => (
              <div key={c.id} className="flex items-center justify-between px-5 py-3 text-sm" data-testid={`cost-${c.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-slate-800 truncate flex items-center gap-2">
                    {c.title}
                    {c.source === "expense" && <span className="text-[10px] font-semibold text-amber-700 bg-amber-50 rounded-full px-2 py-0.5 shrink-0">Expense</span>}
                  </div>
                  <div className="text-[11px] text-slate-400 truncate">
                    {[c.account_type, c.cost_type, c.depot, c.cost_date].filter(Boolean).join(" · ")}
                    {c.quote_number ? ` · Quote ${c.quote_number}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  <span className="font-semibold text-slate-900">{gbp2(c.total)}</span>
                  {c.source !== "expense" && <CostReceiptsButton cost={c} canManage={canManageCosts}
                    onClosed={() => api.get(`/tasks/${task.legacy_id}/costs`).then((r) => setCosts(r.data.items || [])).catch(() => {})} />}
                  {canManageCosts && c.source !== "expense" && (
                    <button onClick={() => delCost(c.id)} className="text-rose-500 hover:bg-rose-50 rounded p-1" data-testid={`cost-del-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
      </>)}

      {costForm && (
        <CostForm taskId={task.legacy_id} quoteNumber={costForm.quoteNumber}
          depots={task?.depot ? [task.depot] : []}
          onClose={() => setCostForm(null)} onSaved={onCostSaved} />
      )}

      {/* Products to fulfil */}
      {assignments.length > 0 && (
        <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="finance-products">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-1.5"><Package className="w-4 h-4 text-emerald-500" /> Products to Fulfil</h2>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-400">{assignments.filter((a) => a.consumed).length}/{assignments.length} fulfilled</span>
              {isAdmin && assignments.some((a) => !a.consumed) && (
                <Button size="sm" disabled={busyAll || busy !== null}
                  className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={fulfilAll} data-testid="fulfil-all-btn">
                  {busyAll ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Fulfilling…</> : "Fulfil all"}
                </Button>
              )}
            </div>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400 border-b border-slate-100"><th className="px-4 py-2">Product</th><th className="px-3 py-2">SKU</th><th className="px-3 py-2 text-center">Qty</th><th className="px-3 py-2 text-right">Weight</th><th className="px-3 py-2 text-center">Status</th>{isAdmin && <th className="px-3 py-2 text-right">Action</th>}</tr></thead>
            <tbody className="divide-y divide-slate-50">
              {assignments.map((p, i) => (
                <tr key={i} data-testid={`finance-product-${i}`}>
                  <td className="px-4 py-2.5 text-slate-800">{p.product}</td>
                  <td className="px-3 py-2.5 text-slate-500 font-mono">{p.sku || "—"}</td>
                  <td className="px-3 py-2.5 text-center text-slate-700">{p.quantity}</td>
                  <td className="px-3 py-2.5 text-right text-slate-600" data-testid={`finance-product-weight-${i}`}>{p.weight != null ? `${+(p.weight * (p.quantity || 1)).toFixed(3)} kg` : "—"}</td>
                  <td className="px-3 py-2.5 text-center">
                    {p.consumed
                      ? <Pill colour="green" dot>{p.stock_deducted ? "Fulfilled · stock -" + p.quantity : "Fulfilled"}</Pill>
                      : <Pill colour="slate">Pending</Pill>}
                  </td>
                  {isAdmin && (
                    <td className="px-3 py-2.5 text-right">
                      <Button size="sm" variant={p.consumed ? "outline" : "default"} disabled={busy === i || busyAll}
                        className={`h-7 text-xs ${p.consumed ? "" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}
                        onClick={() => fulfil(i, !p.consumed)} data-testid={`fulfil-btn-${i}`}>
                        {busy === i ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (p.consumed ? "Undo" : "Fulfil")}
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {/* Products & Materials used from stock */}
      <JobMaterials legacyId={task.legacy_id} />

      {/* Finance Breakdown / Totals */}
      <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="finance-totals">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
          <h2 className="text-sm font-bold text-slate-900">Finance Breakdown</h2>
          <span className="text-sm font-semibold text-emerald-600">{gbp2(jobRevenue - costsTotal)}</span>
        </div>
        <div className="py-2">
          <FinTotalRow label="Shift Earnings" value={gbp2(0)} />
          <FinTotalRow label="Shift Costs" value={gbp2(0)} />
          <FinTotalRow label="Shift Profit" value={gbp2(0)} />
          <FinTotalRow label="Revenue" value={gbp2(jobRevenue)} sub={revenueSource} top />
          <FinTotalRow label="Costs" value={gbp2(costsTotal)} />
          {costs.length > 0 && <CostBreakdownRows costs={costs} gbp={gbp2} />}
          <FinTotalRow label="Gross Profit" value={gbp2(jobRevenue - costsTotal)} tone="text-emerald-600" />
          <FinTotalRow label="Gross Margin %" value={grossMargin != null ? `${grossMargin.toFixed(1)}%` : "—"} tone="text-emerald-600" />
          {wf && <FinTotalRow label="VAT" value={gbp2(vatSum)} top />}
          {wf && <FinTotalRow label="Total (inc VAT)" value={gbp2(grandTotal)} bold />}
          <JobPaymentsSummary legacyId={task.legacy_id} fallbackTotal={jobRevenue} testid="fin-breakdown-payments" />
        </div>
      </section>
    </div>
  );
}

function JobMaterials({ legacyId }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/tasks/${legacyId}/materials`).then((r) => setData(r.data)).catch(() => setData({ items: [], summary: {} })); }, [legacyId]);
  if (!data || (data.items || []).length === 0) return null;
  const s = data.summary || {};
  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="job-materials">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <h2 className="text-sm font-bold text-slate-900 flex items-center gap-1.5"><Package className="w-4 h-4 text-indigo-500" /> Products &amp; Materials Used</h2>
        <span className="text-xs text-slate-400">{s.allocated || 0} allocated · {s.used || 0} used · {s.returned || 0} returned</span>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400 border-b border-slate-100"><th className="px-4 py-2">Date</th><th className="px-3 py-2">Product</th><th className="px-3 py-2">Action</th><th className="px-3 py-2 text-right">Qty</th><th className="px-3 py-2">From</th><th className="px-3 py-2">By</th></tr></thead>
        <tbody className="divide-y divide-slate-50">
          {data.items.map((m) => (
            <tr key={m.movement_id} data-testid={`material-${m.movement_id}`}>
              <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">{new Date(m.ts).toLocaleDateString("en-GB")}</td>
              <td className="px-3 py-2.5 text-slate-800"><Link to={`/products/${m.product_legacy_id}`} className="hover:text-indigo-600">{m.product_title || m.product_legacy_id}</Link></td>
              <td className="px-3 py-2.5"><Pill colour={m.direction === "in" ? "green" : "amber"}>{m.type_label}</Pill></td>
              <td className={`px-3 py-2.5 text-right tabular-nums font-semibold ${m.direction === "in" ? "text-emerald-600" : "text-rose-600"}`}>{m.direction === "in" ? "+" : "−"}{m.quantity}</td>
              <td className="px-3 py-2.5 text-xs text-slate-500">{m.from_location_name || m.to_location_name || "—"}</td>
              <td className="px-3 py-2.5 text-xs text-slate-400 truncate max-w-[120px]">{m.user_email}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

function ShiftEtaPanel({ taskId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = () => {
    setLoading(true);
    api.get(`/tasks/${taskId}/shift-eta`).then((r) => setData(r.data)).catch(() => setData({ available: false, reason: "Could not load ETA." })).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [taskId]);
  const t = data?.traffic;
  const fmtT = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }) : "—");
  const fmtTS = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—");
  return (
    <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="shift-eta-panel">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold text-slate-900 flex items-center gap-2"><Navigation className="w-4 h-4 text-indigo-500" /> Shift-to-shift ETA</h2>
        <button onClick={load} disabled={loading} className="text-xs font-medium text-indigo-600 hover:underline flex items-center gap-1" data-testid="shift-eta-refresh">
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Refresh
        </button>
      </div>
      {!data ? <div className="text-sm text-slate-400">Loading…</div>
        : !data.available ? <div className="text-sm text-slate-400">{data.reason}</div>
        : (
          <>
            <div className="text-sm text-slate-700"><span className="font-medium">{data.from.label}</span> <span className="text-slate-400">→</span> <span className="font-medium">{data.to.label}</span></div>
            {data.origin_kind === "previous_shift" && <div className="text-[11px] text-slate-400 mt-1" data-testid="shift-eta-origin-note">From the previous scheduled shift{data.vehicle_title ? ` on ${data.vehicle_title}` : ""}.</div>}
            {data.origin_kind === "depot" && <div className="text-[11px] text-amber-600 mt-1" data-testid="shift-eta-origin-note">No earlier shift found — measured from the HQ depot.</div>}
            {t?.enabled ? (
              <div className="mt-3 space-y-2" data-testid="shift-eta-live">
                <div className="grid grid-cols-3 gap-2">
                  <MapStat label="Distance" value={`${t.miles} mi`} />
                  <MapStat label="With traffic" value={fmtDur(t.duration_traffic_mins)} tone="text-rose-600" />
                  <MapStat label="Typical" value={t.duration_typical_mins != null ? fmtDur(t.duration_typical_mins) : "—"} />
                </div>
                <div className="flex items-center justify-between text-sm pt-1">
                  <span className="text-slate-500">Arrival ETA</span><span className="font-bold text-slate-900">{fmtT(t.eta_iso)}</span>
                </div>
                {t.delay_mins != null && t.delay_mins > 0 && <div className="text-xs text-amber-600">+{fmtDur(t.delay_mins)} vs typical due to traffic</div>}
                <div className="text-[11px] text-slate-400 pt-1 border-t border-slate-100">Live · Google Routes API · updated {fmtTS(t.computed_at_iso)}</div>
              </div>
            ) : (
              <div className="mt-3 rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-800" data-testid="shift-eta-disabled">
                Live traffic ETA not enabled yet. {t?.reason}
              </div>
            )}
          </>
        )}
    </section>
  );
}

function TaskMapTab({ id }) {
  const [route, setRoute] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    setRoute(null); setErr(false);
    api.get(`/tasks/${id}/route`).then((r) => setRoute(r.data)).catch(() => setErr(true));
  }, [id]);

  const [showAllInc, setShowAllInc] = useState(true);
  const { data: inc } = useRouteIncidents(route?.route?.geometry || [], {
    snapToRoads: false, showAll: showAllInc, enabled: !!(route && route.available && route.route?.geometry),
  });

  if (err) return <div className="p-8 text-slate-500">Could not load the route.</div>;
  if (!route) return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /> Loading route &amp; traffic…</div>;

  return (
    <div className="p-6 lg:p-8 grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="task-map-tab">
      <div className="lg:col-span-2">
        <IncidentWarning data={inc} />
        {route.available ? (
          <TaskRouteMap
            route={route}
            incidents={inc?.incidents || []}
            providers={inc?.providers}
            showAll={showAllInc}
            onShowAllChange={setShowAllInc}
            restrictRegistrations={route.vehicle_registrations}
          />
        ) : (
          <div className="h-[420px] flex items-center justify-center bg-slate-50 rounded-xl border border-dashed border-slate-200 text-slate-400 text-sm text-center px-6" data-testid="task-map-unavailable">
            {route.reason || "No map available for this task."}
          </div>
        )}
        {route.route && (
          <div data-testid="task-route-stats">
            <div className="mt-4 mb-2 text-xs text-slate-500">From <b className="text-slate-700">HQ depot</b>{route.depot?.postcode ? ` (${route.depot.postcode})` : ""} <span className="text-slate-400">→</span> {route.venue?.postcode || "venue"}</div>
            <div className="grid grid-cols-3 gap-3">
              <MapStat label="Distance" value={`${route.route.miles} mi`} />
              <MapStat label={route.traffic_available ? "Typical drive" : "Drive time"} value={fmtDur(route.route.mins)} />
              <MapStat label={route.traffic_available ? "With traffic" : "Peak est. (demo)"} value={fmtDur(route.route.traffic_mins)} tone={route.traffic_available ? "text-rose-600" : "text-amber-600"} />
            </div>
          </div>
        )}
        {route.traffic_note && <p className="text-[11px] text-slate-400 mt-2">{route.traffic_note}</p>}
      </div>

      <div className="space-y-4">
        <ShiftEtaPanel taskId={id} />
        <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5" data-testid="task-day-summary">
          <h2 className="text-base font-bold text-slate-900 mb-4">Day Summary</h2>
          <ol className="relative border-l border-slate-200 ml-2 space-y-4">
            {(route.day_summary || []).map((d, i) => (
              <li key={i} className="ml-4">
                <span className={`absolute -left-[7px] w-3.5 h-3.5 rounded-full border-2 border-white ${d.kind === "depot" ? "bg-slate-800" : "bg-indigo-500"}`} />
                <div className="text-sm font-semibold text-slate-900">{d.label}</div>
                <div className="text-xs text-slate-500">{d.detail}{d.location ? ` · ${d.location}` : ""}</div>
                {d.kind === "shift" && <span className="text-[11px] font-medium text-emerald-600">{d.status}</span>}
              </li>
            ))}
          </ol>
        </section>

        {route.venue && (
          <section className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 text-sm space-y-2">
            <h2 className="text-base font-bold text-slate-900 mb-2">Location</h2>
            <QG label="Depot" value={route.depot ? `${route.depot.name} (${route.depot.postcode})` : "—"} />
            <QG label="Venue" value={route.venue.name} />
            <QG label="Postcode" value={route.venue.postcode} />
            <QG label="District" value={route.venue.district} />
          </section>
        )}
      </div>
    </div>
  );
}

function shiftInitials(name) {
  return name ? name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase() : "?";
}

function ShiftCard({ shift: s, onOpenFull }) {
  const [open, setOpen] = useState(false);
  const req = s.requirements || {};
  const headerClick = onOpenFull || (() => setOpen((o) => !o));
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid={`shift-${s.shift_id}`}>
      <button onClick={headerClick} className="w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors" data-testid={`shift-toggle-${s.shift_id}`}>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <div className="min-w-[150px]">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Date</div>
            <div className="text-sm font-semibold text-slate-800">{s.date_label}</div>
          </div>
          <div className="min-w-[180px]">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Resources</div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-sm text-slate-700 truncate max-w-[140px]">{s.vehicle}</span>
              {(s.staff || []).map((st) => (
                <span key={st} title={st} className="inline-flex items-center justify-center w-6 h-6 rounded-full border border-slate-300 bg-white text-[10px] font-bold text-slate-600">{shiftInitials(st)}</span>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Status</div>
            <div className="mt-0.5"><Pill colour={s.status_colour}>{s.status}</Pill></div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Work Type</div>
            <div className="mt-0.5"><Pill colour={s.work_type_colour} dot>{s.work_type}</Pill></div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Earnings</div>
            <div className="text-sm font-semibold text-rose-500">£{(s.earnings || 0).toFixed(2)}</div>
          </div>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">Invoiced</div>
            <div className="text-sm text-slate-700">{s.invoiced ? "Yes" : "No"}</div>
          </div>
          <div className="flex items-center gap-1.5 text-slate-500 text-sm ml-auto">
            <CloudRain className="w-4 h-4 text-slate-400" />
            <Droplets className="w-3.5 h-3.5 text-slate-400" /> {s.weather?.precip}
            <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${open ? "rotate-90" : ""}`} />
          </div>
        </div>
      </button>

      {open && (
        <div className="border-t border-slate-100 p-4 space-y-5 bg-slate-50/40" data-testid={`shift-detail-${s.shift_id}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <QG label="Shift ID" value={s.shift_id} />
            <QG label="Shift Status" value={s.status} />
            <div className="flex items-center justify-between"><span className="text-slate-500 flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Time</span><span className="text-slate-800 font-medium text-right">{s.date_full} ({s.logins || 0} Logins)</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500 flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5" /> Location</span><span className="text-slate-800 font-medium text-right">{s.location}</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500 flex items-center gap-1.5"><Navigation className="w-3.5 h-3.5" /> Route</span><span className="text-slate-800 font-medium text-right">{s.route?.miles} Miles, {s.route?.mins} Mins</span></div>
            <div className="flex items-center justify-between"><span className="text-slate-500 flex items-center gap-1.5"><CloudRain className="w-3.5 h-3.5" /> Weather</span><span className="text-slate-800 font-medium text-right flex items-center gap-2 justify-end">{s.weather?.label} <Droplets className="w-3.5 h-3.5 text-slate-400" />{s.weather?.precip} <Thermometer className="w-3.5 h-3.5 text-slate-400" />{s.weather?.temp}</span></div>
          </div>

          <div>
            <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1.5 flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Resources</div>
            <div className="flex flex-wrap items-center gap-2">
              <Pill colour="slate" dot={false}>{s.vehicle}</Pill>
              {(s.staff || []).map((st) => <Pill key={st} colour="indigo">{st}</Pill>)}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-sm font-bold text-slate-900">Requirements</div>
              {req.required_count ? <span className="text-xs font-semibold text-rose-600">{req.required_count} Required</span> : null}
            </div>
            {["pre_site", "on_site", "post_site"].map((k) => {
              const grp = req[k]; if (!grp) return null;
              const title = k === "pre_site" ? "Pre Site" : k === "on_site" ? "On Site" : "Post Site Checks";
              return (
                <div key={k} className="mb-3">
                  <div className="flex items-center justify-between text-xs font-semibold text-slate-600 mb-1">
                    <span>{title}</span>
                    <span className="text-amber-600">{grp.status}</span>
                  </div>
                  <div className="space-y-1">
                    {grp.items.map((it) => (
                      <div key={it.label} className="flex items-center justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                        <span className="flex items-center gap-2 text-slate-700"><CircleDot className="w-3.5 h-3.5 text-slate-300" />{it.label}</span>
                        {it.required && <span className="text-xs text-rose-500 font-medium">Required</span>}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {s.product_assignments?.length > 0 && (
            <div data-testid={`shift-products-${s.shift_id}`}>
              <div className="text-sm font-bold text-slate-900 mb-2 flex items-center gap-1.5"><Package className="w-4 h-4 text-emerald-500" /> Product Assignments</div>
              <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-400"><th className="px-3 py-2 w-20">ID</th><th className="px-3 py-2">Product</th><th className="px-3 py-2 w-24 text-center">Consumed</th></tr></thead>
                  <tbody className="divide-y divide-slate-100">
                    {s.product_assignments.map((p) => (
                      <tr key={p.id}><td className="px-3 py-2 font-mono text-slate-500">{p.id}</td><td className="px-3 py-2 text-slate-800">{p.product}</td><td className="px-3 py-2 text-center text-slate-400">✕</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
